import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton, SearchResultsSkeleton, LoadingSpinner } from "@/components/ui/skeleton";
import { customerApi, itemApi, tariffApi, rentalApi } from "@/lib/api";
import { printTicket, getStoredSettings } from "@/lib/ticketGenerator";
import { useSettings } from "@/contexts/SettingsContext";
import { useScannerListener } from "@/hooks/useScannerListener";
import { useMultiTicketPersistence } from "@/hooks/useMultiTicketPersistence";
import { useDebounce, useDebouncedCallback } from "@/hooks/useDebounce";
import { useKeyboardNavigation, useGridNavigation } from "@/hooks/useKeyboardNavigation";
import { FocusTrap } from "@/components/FocusTrap";
import MixedPaymentInput from "@/components/MixedPaymentInput";
import { 
  Search, 
  User, 
  Package, 
  Trash2, 
  Plus,
  Check,
  Loader2,
  History,
  Barcode,
  RotateCcw,
  Calendar,
  Clock,
  ArrowRight,
  Edit2,
  X,
  AlertTriangle,
  AlertCircle,
  Sparkles,
  CheckCircle,
  Printer,
  Eye,
  EyeOff,
  ChevronDown,
  Ruler,
  Scale,
  Mountain,
  Radio,
  Zap,
  Globe
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, getCurrencySymbol } from "@/lib/currency";

// COMPONENTE VIRTUALIZADO para listas de búsqueda
const VirtualizedSearchResults = ({ items, onItemClick }) => {
  const parentRef = useRef(null);
  
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 72, // Altura estimada de cada fila
    overscan: 5, // Elementos extra a renderizar fuera del viewport
  });

  return (
    <div 
      ref={parentRef} 
      className="h-full overflow-y-auto"
      style={{ contain: 'strict' }}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const item = items[virtualRow.index];
          return (
            <div
              key={item.barcode || virtualRow.index}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              className="absolute top-0 left-0 w-full px-2"
              style={{
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div 
                className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => onItemClick(item)}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Package className="h-5 w-5 text-slate-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs truncate max-w-[120px]">{item.item_type}</Badge>
                      <span className="font-mono font-bold text-slate-900 text-sm sm:text-base">{item.internal_code || item.barcode}</span>
                    </div>
                    <p className="font-medium text-slate-900 truncate">
                      {item.brand} {item.model} - {item.size}
                    </p>
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="flex-shrink-0">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      {items.length > 50 && (
        <p className="text-center text-xs text-slate-600 py-2">
          Mostrando {Math.min(items.length, 50)} de {items.length} resultados
        </p>
      )}
    </div>
  );
};

const PAYMENT_METHODS = [
  { value: "card", label: "Tarjeta" },
  { value: "cash", label: "Efectivo" },
  { value: "pending", label: "Pendiente de pago" },
  { value: "pago_online", label: "Pago Online" },
];


// Item types will be loaded from API
// Helper: Get smart start date based on time
const getSmartStartDate = () => {
  const now = new Date();
  const hour = now.getHours();
  if (hour >= 15) {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  }
  return now.toISOString().split('T')[0];
};

const addDays = (dateStr, days) => {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + days - 1);
  return date.toISOString().split('T')[0];
};

const calculateDaysBetween = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffTime = endDate - startDate;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  return Math.max(1, diffDays);
};

const formatDateDisplay = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
};

export default function NewRental() {
  // Settings context for ticket configuration
  const settings = useSettings();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("booking_id");

  // ── Online Booking preload ──────────────────────────────────────────────────
  const [onlineBooking, setOnlineBooking] = useState(null);
  
  // ============ MULTI-TICKET PERSISTENT STATE ============
  // Este hook persiste múltiples tickets en localStorage para que no se pierdan
  const {
    isInitialized: cartInitialized,
    tickets,
    activeTicketId,
    customer,
    customerHistory,
    items,
    detectedPacks,
    numDays,
    startDate,
    endDate,
    notes,
    discountType,
    discountValue,
    discountReason,
    setCustomer,
    setCustomerHistory,
    setItems,
    setDetectedPacks,
    setNumDays,
    setStartDate,
    setEndDate,
    setNotes,
    setDiscountType,
    setDiscountValue,
    setDiscountReason,
    clearCart,
    hasCartData,
    // Multi-ticket functions
    createTicket,
    switchToTicket,
    closeTicket,
    completeAndCloseActiveTicket,
    getTicketDisplayName
  } = useMultiTicketPersistence();
  
  // ============ CROSS-TICKET DUPLICATE CHECK ============
  // Checks if an item (by barcode or internal_code) exists in ANY other open ticket
  const findItemInOtherTicket = useCallback((barcode, internalCode) => {
    if (!tickets || tickets.length <= 1) return null;
    
    for (const ticket of tickets) {
      if (ticket.id === activeTicketId) continue;
      const ticketItems = ticket.data?.items || [];
      const found = ticketItems.find(i => 
        (barcode && (i.barcode === barcode || i.internal_code === barcode)) ||
        (internalCode && (i.barcode === internalCode || i.internal_code === internalCode))
      );
      if (found) {
        return { ticket, item: found };
      }
    }
    return null;
  }, [tickets, activeTicketId]);

  // ============ NON-PERSISTENT STATE (UI/Transient) ============
  const [searchTerm, setSearchTerm] = useState("");
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [packs, setPacks] = useState([]);
  const [barcodeInput, setBarcodeInput] = useState("");
  
  // Smart date system (showTimeHint is transient)
  const [showTimeHint, setShowTimeHint] = useState(true);
  
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paidAmount, setPaidAmount] = useState("");
  const [deposit, setDeposit] = useState("");
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ dni: "", name: "", phone: "", email: "", address: "", city: "", source: "" });
  
  // Item search modal
  const [showItemSearch, setShowItemSearch] = useState(false);
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  const [itemSearchType, setItemSearchType] = useState("all");
  const [searchResults, setSearchResults] = useState([]);
  const [searchingItems, setSearchingItems] = useState(false);
  const [searchFilter, setSearchFilter] = useState(null); // For upselling suggestion filter
  const [packSuggestions, setPackSuggestions] = useState([]); // Smart upselling suggestions
  
  // Success dialog and printing
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [completedRental, setCompletedRental] = useState(null);
  
  // Quick Add items (dynamic from DB)
  const [quickAddItems, setQuickAddItems] = useState([]);
  const [loadingQuickAdd, setLoadingQuickAdd] = useState(false);
  
  // Payment modal (NEW)
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentMethodSelected, setPaymentMethodSelected] = useState("cash");
  const [cashGiven, setCashGiven] = useState("");
  const [processingPayment, setProcessingPayment] = useState(false);
  // Mixed payment state (replaces single-method)
  const [mixedPaymentState, setMixedPaymentState] = useState({ cashAmount: 0, cardAmount: 0, method: "cash", change: 0, isValid: false, mode: "cash" });
  
  // Auto cash opening modal (NEW)
  const [showAutoOpenCashDialog, setShowAutoOpenCashDialog] = useState(false);
  const [openingCashBalance, setOpeningCashBalance] = useState("");
  const [pendingPaymentData, setPendingPaymentData] = useState(null);
  
  // Price editing (transient UI state)
  const [editingItemPrice, setEditingItemPrice] = useState(null);
  const [editingItemDays, setEditingItemDays] = useState(null);
  const [editingPackPrice, setEditingPackPrice] = useState(null); // Moved here for scanner disable check
  const [editingItemType, setEditingItemType] = useState(null); // Track which item is editing its type
  const [editingPackName, setEditingPackName] = useState(null); // Track which pack is editing its name
  
  // Ref to track previous items for pack stability
  const prevItemsRef = useRef([]);
  
  // Pack expansion state - which pack is currently expanded to show components
  const [expandedPackId, setExpandedPackId] = useState(null);
  
  // Customer technical data visibility toggle
  const [showCustomerTechData, setShowCustomerTechData] = useState(false);
  
  // Sources
  const [sources, setSources] = useState([]);
  
  // Item types from API
  const [itemTypes, setItemTypes] = useState([]);
  
  // ============ REFS PARA NAVEGACIÓN POR TECLADO ============
  const barcodeRef = useRef(null);      // Campo de código de barras
  const searchRef = useRef(null);       // Buscador de cliente
  const daysRef = useRef(null);         // Selector de días
  const customerSearchRef = useRef(null); // Input de búsqueda de cliente
  const submitRef = useRef(null);       // Botón de cobrar
  const mainContainerRef = useRef(null); // Contenedor principal para navegación
  const cartGridRef = useRef(null);     // Grid de artículos del carrito

  // Check if any cart item is being edited (to disable global scanner and Tab navigation)
  const isEditingCartItem = editingItemDays !== null || editingItemPrice !== null || editingPackPrice !== null;

  // ============ NAVEGACIÓN POR TECLADO GLOBAL ============
  // Hook para navegación tipo spreadsheet con flechas, Enter como Tab
  // IMPORTANTE: Deshabilitado cuando hay sugerencias de cliente visibles
  useKeyboardNavigation({
    containerRef: mainContainerRef,
    enabled: !showItemSearch && !showNewCustomer && !showPaymentDialog && !showSuccessDialog && !isEditingCartItem && !showSuggestions,
    enterAsTab: true,
    arrowNavigation: true,
    gridMode: false,
    onNavigate: (direction, element) => {
      console.log('[KEYBOARD NAV] Direction:', direction, 'Element:', element?.tagName);
    }
  });

  // Hook especializado para navegación en la tabla del carrito
  useGridNavigation({
    gridRef: cartGridRef,
    enabled: !showItemSearch && !showNewCustomer && !showPaymentDialog && !showSuggestions,
    onCellChange: (element, direction) => {
      console.log('[GRID NAV] Cell change:', direction, element?.name || element?.tagName);
    }
  });

  // Secuencia de navegación: Cliente -> Días -> Código -> Cobrar
  const focusNextField = useCallback((currentField) => {
    const sequence = {
      'customer': daysRef,
      'days': barcodeRef,
      'barcode': submitRef,
      'submit': customerSearchRef
    };
    const nextRef = sequence[currentField];
    if (nextRef?.current) {
      nextRef.current.focus();
      if (nextRef.current.select) nextRef.current.select();
    }
  }, []);

  const focusPrevField = useCallback((currentField) => {
    const sequence = {
      'days': customerSearchRef,
      'barcode': daysRef,
      'submit': barcodeRef,
      'customer': submitRef
    };
    const prevRef = sequence[currentField];
    if (prevRef?.current) {
      prevRef.current.focus();
      if (prevRef.current.select) prevRef.current.select();
    }
  }, []);

  // ─── PRICE AT ADD TIME ──────────────────────────────────────────────────────
  // Computes the tariff-based price for an item at a given day count.
  // Called once when the item enters the cart, result stored as calculatedPrice.
  // Defined here (before scanner hooks) to be available in handleGlobalScan closure.
  const computeItemPrice = useCallback((item, days) => {
    const d = days || numDays || 1;
    if (item.customPrice !== null && item.customPrice !== undefined) return item.customPrice;
    if (item.is_generic && item.rental_price !== null && item.rental_price !== undefined) return item.rental_price;
    if (!item.is_generic && item.rental_price !== null && item.rental_price !== undefined) return item.rental_price;
    const tariff = tariffs.find(t => t.item_type === item.item_type);
    if (tariff) {
      const dayField = d <= 10 ? `day_${d}` : 'day_11_plus';
      return tariff[dayField] || tariff.day_1 || 0;
    }
    return 0;
  }, [tariffs, numDays]);

  // ============ GLOBAL SCANNER LISTENER (HID BARCODE READER) ============
  // Captura entrada de lectores HID como Netum NT-1698W cuando el cursor no está en un input
  const handleGlobalScan = useCallback(async (scannedCode) => {
    console.log('[SCANNER] Global scan detected in NewRental:', scannedCode);
    
    // Set the barcode input for visual feedback
    setBarcodeInput(scannedCode);
    
    // Process the scanned code (add item to rental)
    try {
      const response = await itemApi.getByBarcode(scannedCode);
      const item = response.data;
      
      if (item.status !== 'available') {
        toast.error(`Artículo no disponible (${item.status})`);
        setBarcodeInput("");
        return;
      }
      
      if (items.find(i => i.barcode === item.barcode)) {
        toast.error("Artículo ya añadido");
        setBarcodeInput("");
        return;
      }
      
      // Check if item is in another open ticket
      const otherTicket = findItemInOtherTicket(item.barcode, item.internal_code);
      if (otherTicket) {
        toast.error(`Artículo ya está en otro ticket abierto`);
        setBarcodeInput("");
        return;
      }
      
      setItems(prev => [...prev, { ...item, customPrice: null, itemDays: numDays, calculatedPrice: computeItemPrice(item, numDays) }]);
      toast.success(`${item.brand} ${item.model} añadido`);
      setBarcodeInput("");
    } catch (error) {
      toast.error("Artículo no encontrado");
      setBarcodeInput("");
    }
  }, [items, numDays, computeItemPrice]);
  
  const { isScanning: globalScannerActive, forceFocus: focusBarcodeField } = useScannerListener({
    onScan: handleGlobalScan,
    inputRef: barcodeRef,
    // IMPORTANT: Disable scanner when editing cart items to prevent interference
    enabled: settings.quickScanMode !== false && !showItemSearch && !showNewCustomer && !showPaymentDialog && !showSuccessDialog && !isEditingCartItem,
    minLength: 3,
    maxTimeBetweenKeys: 50,
    scannerDetectionThreshold: 4,
    autoFocus: !isEditingCartItem, // Don't auto-focus when editing
  });

  useEffect(() => {
    loadTariffs();
    loadSources();
    loadPacks();
    syncAndLoadItemTypes();
    const timer = setTimeout(() => setShowTimeHint(false), 5000);
    
    // AUTO-FOCUS: Focus barcode input on page load (for barcode scanner)
    setTimeout(() => {
      if (barcodeRef.current) {
        barcodeRef.current.focus();
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  // ── Load online booking if booking_id is in URL ─────────────────────────
  useEffect(() => {
    if (!bookingId || !cartInitialized) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    axios.get(`${API_URL}/api/online-bookings/${bookingId}`, { headers })
      .then(({ data }) => {
        setOnlineBooking(data);
        // Pre-fill dates from booking
        if (data.start_date) setStartDate(data.start_date);
        if (data.end_date) setEndDate(data.end_date);

        // Cobro Online Cotejado — prefill the deposit input with whatever the
        // customer already paid on the web so the clerk doesn't retype it.
        // `amount` (importe pagado online) is used only for the "A cobrar"
        // display in the footer and to prefill paidAmount in the payment
        // dialog. Rental math itself is unchanged.
        if (data.deposit !== null && data.deposit !== undefined && Number(data.deposit) > 0) {
          setDeposit(String(data.deposit));
        }

        // Match/create the customer server-side and auto-select the ficha.
        // This runs BEFORE the ticket is finalised, so cancelled bookings
        // never reach this branch (the endpoint refuses them).
        return axios.post(
          `${API_URL}/api/online-bookings/${bookingId}/customer-match-or-create`,
          {},
          { headers }
        )
          .then(({ data: match }) => {
            if (match?.customer) {
              setCustomer(match.customer);
              if (match.created) {
                toast.success(`Reserva ${data.localizador} — ficha creada para ${match.customer.name}`);
              } else {
                toast.info(`Reserva ${data.localizador} — cliente vinculado: ${match.customer.name}`);
              }
            } else {
              // Fallback: at least prefill the search field
              if (data.client_name) setSearchTerm(data.client_name);
              toast.info(`Reserva ${data.localizador} cargada`);
            }
          })
          .catch((err) => {
            console.warn("[OnlineBooking] Could not match/create customer:", err?.message);
            // Non-fatal: still let the clerk continue by hand.
            if (data.client_name) setSearchTerm(data.client_name);
            toast.info(`Reserva ${data.localizador} cargada`);
          });
      })
      .catch(() => {
        toast.error("No se pudo cargar la reserva online");
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, cartInitialized]);

  // AUTO-FOCUS after customer selection: Move focus to barcode input
  useEffect(() => {
    if (customer) {
      // Small delay to let customer card render, then focus barcode
      setTimeout(() => {
        if (barcodeRef.current) {
          barcodeRef.current.focus();
          barcodeRef.current.select();
        }
      }, 300);
    }
  }, [customer]);

  // AUTO-COMBO: Detect packs whenever items change
  // BUT: Respect manually overridden pack groups (manualOverride flag)
  useEffect(() => {
    const itemsWithForcedPack = items.filter(i => i.forcedPackId);
    
    if (itemsWithForcedPack.length > 0) {
      // Group items by their forcedPackId
      const forcedPackGroups = {};
      itemsWithForcedPack.forEach(item => {
        const packId = item.forcedPackId;
        if (!forcedPackGroups[packId]) {
          const pack = packs.find(p => (p._id || p.id) === packId);
          if (pack) {
            forcedPackGroups[packId] = {
              pack: pack,
              items: [],
              instanceId: `forced-pack-${packId}-${Date.now()}`
            };
          }
        }
        if (forcedPackGroups[packId]) {
          forcedPackGroups[packId].items.push(item.barcode);
        }
      });
      
      // Items without forcedPackId - detect their packs normally
      setDetectedPacks(() => {
        const itemsWithoutForcedPack = items.filter(i => !i.forcedPackId);
        
        // Only run auto-detection if there are items WITHOUT a forced pack
        let autoDetected = [];
        if (itemsWithoutForcedPack.length > 0) {
          autoDetected = detectPacks(itemsWithoutForcedPack, []);
        }
        
        const allPacks = [...Object.values(forcedPackGroups), ...autoDetected];
        return allPacks;
      });
    } else {
      // No forced packs - use automatic detection, but preserve manual overrides
      setDetectedPacks(prev => {
        // PRESERVE packs manually overridden by the user (pack type change)
        const manualPacks = prev.filter(dp => dp.manualOverride);
        const manualBarcodes = new Set(manualPacks.flatMap(dp => dp.items));
        
        // Only auto-detect for items NOT already in a manual pack
        const itemsForAutoDetect = items.filter(i => !manualBarcodes.has(i.barcode));
        const autoDetected = detectPacks(itemsForAutoDetect, prev.filter(dp => !dp.manualOverride));
        
        const allPacks = [...manualPacks, ...autoDetected];
        
        if (JSON.stringify(allPacks) !== JSON.stringify(prev)) {
          return allPacks;
        }
        return prev;
      });
    }
  }, [items, packs, numDays]);

  // Load Quick Add items from DB
  useEffect(() => {
    const loadQuickAddItems = async () => {
      setLoadingQuickAdd(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/items/quick-add`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        setQuickAddItems(response.data);
      } catch (error) {
        console.error("Error loading quick add items:", error);
      } finally {
        setLoadingQuickAdd(false);
      }
    };
    loadQuickAddItems();
  }, []);

  // Keyboard shortcut for item search (F3 or Alt+B)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F3' || (e.altKey && e.key === 'b')) {
        e.preventDefault();
        setShowItemSearch(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const loadTariffs = async () => {
    try {
      const response = await tariffApi.getAll();
      setTariffs(response.data);
    } catch (error) {
      console.error("Error loading tariffs:", error);
    }
  };

  const loadSources = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sources`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSources(data);
      }
    } catch (error) {
      console.log("Sources not loaded");
    }
  };

  const loadPacks = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/packs`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPacks(data);
      }
    } catch (error) {
      console.log("Packs not loaded");
    }
  };

  const syncAndLoadItemTypes = async () => {
    try {
      // 🔄 SELF-HEALING: Sincronizar tipos del inventario primero
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/item-types/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Luego cargar los tipos actualizados
      await loadItemTypes();
    } catch (error) {
      console.error("Error syncing item types:", error);
      // Si falla la sync, intentar cargar tipos de todos modos
      loadItemTypes();
    }
  };

  const loadItemTypes = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/item-types`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setItemTypes([{ value: "all", label: "Todos" }, ...data.map(t => ({ value: t.value, label: t.label }))]);
      }
    } catch (error) {
      console.log("Item types not loaded");
      setItemTypes([{ value: "all", label: "Todos" }]);
    }
  };

  // AUTO-COMBO: Detect packs formed by current items
  // IMPORTANT: Once a pack is formed, it stays LOCKED. We only form NEW packs with items not already in packs.
  // This prevents the system from "undoing" existing packs to optimize for a better pack.
  const detectPacks = (currentItems, existingPacks = []) => {
    if (!packs || packs.length === 0 || currentItems.length === 0) {
      return existingPacks; // Return existing packs if no new detection possible
    }

    // STEP 1: Preserve existing packs (LOCKED)
    // Extract barcodes from all existing packs to mark them as "used"
    const usedBarcodes = new Set();
    const lockedPacks = [...existingPacks]; // Keep all existing packs
    
    existingPacks.forEach(pack => {
      pack.items.forEach(barcode => usedBarcodes.add(barcode));
    });
    
    console.log('[PACK DETECTION] Starting with', existingPacks.length, 'locked packs');
    console.log('[PACK DETECTION] Used barcodes from locked packs:', Array.from(usedBarcodes));
    
    // STEP 2: Detect NEW packs ONLY with items NOT already in a pack
    const detectedPackInstances = [];
    
    // Keep trying to form packs until no more can be formed
    let foundPack = true;
    while (foundPack) {
      foundPack = false;
      
      // Try each pack definition
      for (const pack of packs) {
        // Get available items (not yet used in ANY pack - existing OR new)
        const availableItems = currentItems.filter(
          item => !usedBarcodes.has(item.barcode) && !usedBarcodes.has(item.id)
        );
        
        if (availableItems.length === 0) {
          continue; // No items left to form packs
        }
        
        // Count available item types
        const availableTypeCounts = availableItems.reduce((acc, item) => {
          acc[item.item_type] = (acc[item.item_type] || 0) + 1;
          return acc;
        }, {});
        
        // Count required components for this pack
        const requiredComponents = pack.items.reduce((acc, itemType) => {
          acc[itemType] = (acc[itemType] || 0) + 1;
          return acc;
        }, {});

        // Check if we have all components available
        let canFormPack = true;
        for (const [type, count] of Object.entries(requiredComponents)) {
          if ((availableTypeCounts[type] || 0) < count) {
            canFormPack = false;
            break;
          }
        }

        if (canFormPack) {
          // Find the specific items that will form THIS instance of the pack
          const packInstanceItems = [];
          
          for (const requiredType of pack.items) {
              const item = availableItems.find(i => 
                i.item_type === requiredType && 
                !packInstanceItems.includes(i.barcode) &&
                !usedBarcodes.has(i.barcode) &&
                !usedBarcodes.has(i.id)
              );
              if (item) {
                const itemBarcode = item.barcode || item.id;
                packInstanceItems.push(itemBarcode);
                usedBarcodes.add(itemBarcode);
              }
            }

            if (packInstanceItems.length === pack.items.length) {
              // Generate unique instance ID for this pack
              const instanceId = `pack-instance-${Date.now()}-${detectedPackInstances.length}`;
              
              detectedPackInstances.push({
                pack: pack,
                items: packInstanceItems,
                instanceId: instanceId  // Unique ID for this specific pack instance
              });
              
              console.log('[PACK DETECTION] NEW pack formed:', pack.name, 'with items:', packInstanceItems);
              
              foundPack = true;
              break; // Start over to check for more packs
            }
          }
        }
      }

    // STEP 3: Return locked packs + newly detected packs
    const allPacks = [...lockedPacks, ...detectedPackInstances];
    console.log('[PACK DETECTION] Total packs:', allPacks.length, '(', lockedPacks.length, 'locked +', detectedPackInstances.length, 'new)');
    
    return allPacks;
  };

  // SMART UPSELLING: Detect partial packs (when missing components to complete a pack)
  const detectPartialPacks = (currentItems) => {
    if (!packs || packs.length === 0 || currentItems.length === 0) {
      return [];
    }

    const suggestions = [];
    
    // Try to detect partial packs with all available packs (regardless of category)
    // Since all individual items are now "STANDARD", we don't group by category
    
    // Count item types in current items
    const itemTypeCounts = currentItems.reduce((acc, item) => {
      acc[item.item_type] = (acc[item.item_type] || 0) + 1;
      return acc;
    }, {});

    // Check each pack to see if we can form it by adding items
    packs.forEach(pack => {
      // Count required components for this pack
      const requiredComponents = pack.items.reduce((acc, itemType) => {
        acc[itemType] = (acc[itemType] || 0) + 1;
        return acc;
      }, {});

      // Find what's missing
      const missingItems = [];
      let hasAtLeastOne = false;
      let canComplete = true;

      for (const [type, requiredCount] of Object.entries(requiredComponents)) {
        const currentCount = itemTypeCounts[type] || 0;
        if (currentCount > 0) {
          hasAtLeastOne = true;
        }
        if (currentCount < requiredCount) {
          for (let i = 0; i < (requiredCount - currentCount); i++) {
            missingItems.push(type);
          }
        }
      }

      // Only suggest if we have at least one component but not all
      if (hasAtLeastOne && missingItems.length > 0 && missingItems.length < pack.items.length) {
        // Calculate individual prices for current items
        const currentItemsPrice = currentItems.reduce((sum, item) => {
          return sum + getItemPrice(item);
        }, 0);

        // Calculate what we'd pay if we completed the pack
        const packPrice = getPackPrice(pack);
        
        // Estimate individual price for missing items (average)
        const avgMissingPrice = currentItemsPrice / currentItems.length;
        const estimatedTotalWithoutPack = currentItemsPrice + (avgMissingPrice * missingItems.length);
        
        // Calculate potential savings
        const potentialSavings = estimatedTotalWithoutPack - packPrice;

        if (potentialSavings > 0) {
          suggestions.push({
            pack: pack,
            missingItems: [...new Set(missingItems)], // Unique missing types
            missingCount: missingItems.length,
            currentItems: currentItems.map(i => i.item_type),
            packPrice: packPrice,
            potentialSavings: potentialSavings,
            packName: pack.name
          });
        }
      }
    });

    // Return suggestions sorted by highest savings
    return suggestions.sort((a, b) => b.potentialSavings - a.potentialSavings).slice(0, 3);
  };

  // Update suggestions when items change
  useEffect(() => {
    if (items.length > 0 && packs.length > 0) {
      const suggestions = detectPartialPacks(items);
      setPackSuggestions(suggestions);
    } else {
      setPackSuggestions([]);
    }
  }, [items, packs, numDays]);

  // Open item search with pre-filter for missing item
  const openSearchForMissingItem = (itemType, category) => {
    setItemSearchType(itemType);
    setSearchFilter({ type: itemType });
    setShowItemSearch(true);
  };

  // AUTO-COMBO: Calculate price with pack detection
  const getItemPriceWithPack = (item) => {
    // Check if this item is part of a detected pack
    for (const detectedPack of detectedPacks) {
      if (detectedPack.items.includes(item.barcode)) {
        // This item is part of a pack — use the pack's actual days.
        // `detectedPack.items` is an array of BARCODES (strings), so we have to
        // look up a real item object in the cart to read its itemDays.
        // Prefer the current item's own itemDays (it belongs to this pack),
        // fall back to any sibling item's itemDays, finally to global numDays.
        const siblingWithDays = items.find(
          (it) =>
            detectedPack.items.includes(it.barcode) &&
            typeof it.itemDays === "number"
        );
        const packDays =
          (typeof item.itemDays === "number" ? item.itemDays : null) ??
          siblingWithDays?.itemDays ??
          numDays;
        const packPrice = getPackPrice(detectedPack.pack, packDays);
        // Divide pack price equally among components
        return packPrice / detectedPack.pack.items.length;
      }
    }

    // Not part of a pack, use individual pricing
    return getItemPrice(item);
  };

  const getPackPrice = (pack, days = null) => {
    // Use provided days or fall back to global numDays
    const targetDays = days !== null ? days : numDays;
    
    if (targetDays <= 10 && pack[`day_${targetDays}`]) {
      return pack[`day_${targetDays}`];
    }
    if (targetDays > 10 && pack.day_11_plus) {
      return pack.day_11_plus;
    }
    // Fallback to day_1 if available
    return pack.day_1 || 0;
  };

  // Smart date handlers
  const handleNumDaysChange = (value) => {
    const days = Math.max(1, parseInt(value) || 1);
    const previousDays = numDays;
    setNumDays(days);
    setEndDate(addDays(startDate, days));
    
    // UPDATE ALL ITEMS IN CART that haven't been manually edited
    // Items where itemDays equals the previous global value get updated
    if (items.length > 0) {
      setItems(prev => prev.map(item => {
        // If item's days match the previous global value, update it
        // This means it wasn't manually edited
        if (item.itemDays === previousDays || !item.manualDaysEdit) {
          return {
            ...item,
            itemDays: days,
            // Recalculate tariff price for the new duration (custom prices are preserved)
            calculatedPrice: (item.customPrice !== null && item.customPrice !== undefined)
              ? item.customPrice
              : computeItemPrice(item, days)
          };
        }
        // Keep manually edited items unchanged
        return item;
      }));
      toast.info(`Duración actualizada a ${days} día${days !== 1 ? 's' : ''} para todos los artículos`);
    }
    
    if (days > 30) {
      toast.info("Alquiler de larga duración: " + days + " días");
    }
  };

  const handleStartDateChange = (value) => {
    const today = new Date().toISOString().split('T')[0];
    if (value < today) {
      toast.error("No se puede crear un alquiler con fecha de inicio anterior a hoy");
      return;
    }
    setStartDate(value);
    setEndDate(addDays(value, numDays));
  };

  const handleEndDateChange = (value) => {
    const today = new Date().toISOString().split('T')[0];
    if (value < today) {
      toast.error("La fecha de fin no puede ser anterior a hoy");
      return;
    }
    setEndDate(value);
    const calculatedDays = calculateDaysBetween(startDate, value);
    setNumDays(calculatedDays);
  };

  const handleDaysKeyDown = (e) => {
    if (e.key === 'Enter' && barcodeRef.current) {
      e.preventDefault();
      barcodeRef.current.focus();
    }
  };

  const getTimeHintMessage = () => {
    const now = new Date();
    const hour = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const timeStr = `${hour}:${minutes}h`;
    
    if (hour >= 15) {
      return `Inicio mañana porque son las ${timeStr}`;
    }
    return `Inicio hoy porque son las ${timeStr}`;
  };

  const searchCustomer = async () => {
    if (!searchTerm.trim()) return;
    
    setSearchLoading(true);
    try {
      try {
        const response = await customerApi.getByDni(searchTerm);
        selectCustomer(response.data);
        toast.success(`Cliente encontrado: ${response.data.name}`);
        return;
      } catch (e) {
        // Customer not found by DNI, try general search
      }
      
      const response = await customerApi.getAll(searchTerm);
      if (response.data.length === 1) {
        selectCustomer(response.data[0]);
        toast.success(`Cliente encontrado: ${response.data[0].name}`);
      } else if (response.data.length > 1) {
        toast.info("Múltiples clientes encontrados. Sé más específico.");
      } else {
        setNewCustomer({ ...newCustomer, dni: searchTerm.toUpperCase() });
        setShowNewCustomer(true);
      }
    } catch (error) {
      setNewCustomer({ ...newCustomer, dni: searchTerm.toUpperCase() });
      setShowNewCustomer(true);
    } finally {
      setSearchLoading(false);
    }
  };

  // NEW: Predictive search (real-time)
  const searchCustomersRealTime = async (query) => {
    if (!query || query.trim().length < 2) {
      setCustomerSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    try {
      const response = await customerApi.getAll(query.trim());
      setCustomerSuggestions(response.data.slice(0, 8)); // Limit to 8 suggestions
      setShowSuggestions(response.data.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      setCustomerSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // NEW: Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchCustomersRealTime(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // NEW: Select customer from suggestions
  const selectCustomer = async (selectedCustomer) => {
    setCustomer(selectedCustomer);
    setSearchTerm("");
    setShowSuggestions(false);
    setSelectedIndex(-1);
    setShowCustomerTechData(false); // Reset tech data visibility
    
    // Load history with alerts
    try {
      const response = await customerApi.getHistory(selectedCustomer.id);
      setCustomerHistory(response.data);
      
      // Show alert if customer has overdue rentals
      if (response.data.has_alerts && response.data.overdue_rentals > 0) {
        toast.warning(`⚠️ ALERTA: Este cliente tiene ${response.data.overdue_rentals} alquiler(es) vencido(s)`, {
          duration: 5000
        });
      }
    } catch (error) {
      console.error("Error loading history:", error);
    }
  };

  // NEW: Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || customerSuggestions.length === 0) {
      if (e.key === 'Enter') {
        searchCustomer();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < customerSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < customerSuggestions.length) {
          selectCustomer(customerSuggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const loadCustomerHistory = async (customerId) => {
    try {
      const response = await customerApi.getHistory(customerId);
      setCustomerHistory(response.data);
    } catch (error) {
      console.error("Error loading history:", error);
    }
  };

  const createNewCustomer = async () => {
    if (!newCustomer.dni || !newCustomer.name) {
      toast.error("DNI y nombre son obligatorios");
      return;
    }
    
    try {
      const response = await customerApi.create(newCustomer);
      setCustomer(response.data);
      setShowNewCustomer(false);
      setNewCustomer({ dni: "", name: "", phone: "", email: "", address: "", city: "", source: "" });
      toast.success("Cliente creado correctamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear cliente");
    }
  };

  // Item search functions
  // OPTIMIZACIÓN: Debounce del término de búsqueda (250ms)
  const debouncedSearchTerm = useDebounce(itemSearchTerm, 250);
  
  // OPTIMIZACIÓN: AbortController para cancelar peticiones anteriores
  const searchAbortController = useRef(null);
  
  const searchItems = async () => {
    // Cancelar petición anterior si existe
    if (searchAbortController.current) {
      searchAbortController.current.abort();
    }
    searchAbortController.current = new AbortController();
    
    setSearchingItems(true);
    try {
      const params = new URLSearchParams();
      params.append('status', 'available');
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      if (itemSearchType && itemSearchType !== 'all') params.append('item_type', itemSearchType);
      
      // OPTIMIZACIÓN: Limitar resultados a 50 para rendimiento
      params.append('limit', '50');
      params.append('_t', Date.now().toString());
      
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/items?${params.toString()}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache'
        },
        signal: searchAbortController.current.signal
      });
      
      // Filter out already added items
      const addedBarcodes = new Set(items.map(i => i.barcode));
      const addedCodes = new Set(items.map(i => i.internal_code));
      
      // OPTIMIZACIÓN: Limpiar estado anterior antes de asignar nuevo
      setSearchResults(prev => {
        // Clear previous results from memory
        prev.length = 0;
        return response.data.filter(i => 
          !addedBarcodes.has(i.barcode) && !addedCodes.has(i.internal_code)
        );
      });
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        // Petición cancelada, ignorar
        return;
      }
      console.error("Error searching items:", error);
      toast.error("Error al buscar artículos");
    } finally {
      setSearchingItems(false);
    }
  };

  // OPTIMIZACIÓN: Solo buscar cuando el debounce se completa
  useEffect(() => {
    if (showItemSearch) {
      searchItems();
    }
    
    // Cleanup: cancelar peticiones pendientes al cerrar
    return () => {
      if (searchAbortController.current) {
        searchAbortController.current.abort();
      }
    };
  }, [showItemSearch, debouncedSearchTerm, itemSearchType]);

  const addItemFromSearch = (item) => {
    const cartId = `cart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // For generic items: allow multiple independent rows (different days possible)
    // For unique items: prevent duplicates
    if (!item.is_generic) {
      if (items.find(i => i.barcode === item.barcode || i.internal_code === item.internal_code)) {
        toast.error("Artículo ya añadido");
        return;
      }
      const otherTicket = findItemInOtherTicket(item.barcode, item.internal_code);
      if (otherTicket) {
        toast.error(`Artículo ya está en otro ticket abierto`);
        return;
      }
    } else {
      // Generic: check stock
      const alreadyInCart = items.filter(i => i.id === item.id).length;
      if (item.stock_available <= alreadyInCart) {
        toast.error(`Stock máximo alcanzado (${item.stock_available} uds)`);
        return;
      }
    }
    setItems(prev => [...prev, { ...item, cartId, customPrice: null, itemDays: numDays, calculatedPrice: computeItemPrice(item, numDays) }]);
    setSearchResults(searchResults.filter(i => i.barcode !== item.barcode));
    refocusBarcodeInput();
  };

  const addItemByBarcode = async (e) => {
    if (e.key !== 'Enter' || !barcodeInput.trim()) return;
    
    // SANITIZACIÓN: Limpiar entrada (espacios, caracteres invisibles)
    const cleanCode = barcodeInput.trim().replace(/[\s\u200B-\u200D\uFEFF]/g, '');
    if (!cleanCode) {
      setBarcodeInput("");
      refocusBarcodeInput();
      return;
    }
    
    try {
      const response = await itemApi.getByBarcode(cleanCode);
      const item = response.data;
      
      const cartId = `cart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      if (item.status !== 'available' && !item.is_generic) {
        const statusLabels = {
          'rented': 'Alquilado',
          'maintenance': 'En mantenimiento', 
          'retired': 'Retirado'
        };
        toast.error(`Artículo no disponible (${statusLabels[item.status] || item.status})`);
        setBarcodeInput("");
        refocusBarcodeInput();
        return;
      }
      
      if (item.is_generic) {
        // Generic: allow multiple, check stock
        const alreadyInCart = items.filter(i => i.id === item.id).length;
        if (item.stock_available <= alreadyInCart) {
          toast.error(`Stock máximo alcanzado (${item.stock_available} uds)`);
          setBarcodeInput("");
          refocusBarcodeInput();
          return;
        }
      } else {
        // Unique item: prevent duplicates
        if (items.find(i => i.barcode === item.barcode || i.internal_code === item.internal_code)) {
          toast.error("Artículo ya añadido");
          setBarcodeInput("");
          refocusBarcodeInput();
          return;
        }
        const otherTicket = findItemInOtherTicket(item.barcode, item.internal_code);
        if (otherTicket) {
          toast.error(`Artículo ya está en otro ticket abierto`);
          setBarcodeInput("");
          refocusBarcodeInput();
          return;
        }
      }
      
      setItems(prev => [...prev, { ...item, cartId, customPrice: null, itemDays: numDays, calculatedPrice: computeItemPrice(item, numDays) }]);
      toast.success(`✓ ${item.internal_code || item.name || item.barcode}`);
      setBarcodeInput("");
      refocusBarcodeInput();
    } catch (error) {
      // Show more helpful error
      toast.error(`No encontrado: "${cleanCode}"`);
      setBarcodeInput("");
      refocusBarcodeInput();
    }
  };

  // HELPER: Re-focus barcode input with text selection (for barcode scanner optimization)
  const refocusBarcodeInput = () => {
    setTimeout(() => {
      if (barcodeRef.current) {
        barcodeRef.current.focus();
        barcodeRef.current.select(); // Select all text so next scan overwrites
      }
    }, 50);
  };

  // Helper: get the unique cart key for an item (cartId takes priority)
  const getCartKey = (item) => item.cartId || item.id || item.barcode;

  // BORRADO EN CASCADA: Eliminar un pack completo con todos sus componentes
  const removePackComplete = (packItems) => {
    const packCartKeys = new Set(packItems.map(i => getCartKey(i)));
    const packBarcodes = new Set(packItems.map(i => i.barcode).filter(Boolean));

    setItems(prevItems => prevItems.filter(item => !packCartKeys.has(getCartKey(item))));

    setDetectedPacks(prev => prev.filter(dp => 
      !dp.items.some(barcode => packBarcodes.has(barcode))
    ));
  };

  // Eliminar un item - Si es parte de pack, elimina todo el pack
  const removeItem = (cartKey) => {
    const item = items.find(i => getCartKey(i) === cartKey);
    const itemBarcode = item?.barcode;
    
    for (const dp of detectedPacks) {
      if (dp.items.includes(itemBarcode) || dp.items.includes(item?.id)) {
        const packItemObjects = items.filter(i => 
          dp.items.includes(i.barcode) || dp.items.includes(i.id)
        );
        removePackComplete(packItemObjects);
        toast.info(`Pack "${dp.pack.name}" eliminado completamente`);
        return;
      }
    }
    
    // No es parte de ningún pack - eliminar solo este item
    setItems(prev => prev.filter(i => getCartKey(i) !== cartKey));
  };

  // Update item days (marks as manually edited) - uses cartKey for independence
  const updateItemDays = (cartKey, newDays) => {
    // Validate: only positive integers allowed
    const days = parseInt(newDays);
    if (isNaN(days) || days < 1) {
      toast.error("Los días deben ser un número positivo");
      setEditingItemDays(null);
      return;
    }
    setItems(prev => prev.map(item => 
      getCartKey(item) === cartKey 
        ? { ...item, itemDays: days, manualDaysEdit: true }
        : item
    ));
    setEditingItemDays(null);
  };

  // QUICK ADD: Dynamic from database (items with is_quick_add = true)
  const quickAddItem = async (quickItem) => {
    try {
      const cartId = `cart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      if (quickItem.is_generic) {
        // === GENERIC ITEM (stock-based) ===
        // Count how many of this item are already in cart to check against stock
        const alreadyInCart = items.filter(i => i.id === quickItem.id).length;
        if (quickItem.stock_available <= alreadyInCart) {
          toast.error(`Stock máximo alcanzado (${quickItem.stock_available} uds)`);
          return;
        }
        // Each unit = independent row with its own cartId and days
        setItems(prev => [...prev, { 
          ...quickItem, 
          cartId,
          quantity: 1, 
          customPrice: null,
          itemDays: numDays,
          calculatedPrice: computeItemPrice(quickItem, numDays)
        }]);
        toast.success(`✓ ${quickItem.name || quickItem.item_type}`);
      } else {
        // === INDIVIDUAL ITEM (unique, not stock-based) ===
        if (quickItem.status !== 'available') {
          toast.error(`Artículo no disponible (${quickItem.status})`);
          return;
        }
        if (items.find(i => i.id === quickItem.id || i.barcode === quickItem.barcode)) {
          toast.error("Artículo ya añadido");
          return;
        }
        const otherTicket = findItemInOtherTicket(quickItem.barcode, quickItem.internal_code);
        if (otherTicket) {
          toast.error(`Artículo ya está en otro ticket abierto`);
          return;
        }
        setItems(prev => [...prev, { 
          ...quickItem, 
          cartId,
          customPrice: null,
          itemDays: numDays,
          calculatedPrice: computeItemPrice(quickItem, numDays)
        }]);
        toast.success(`✓ ${quickItem.internal_code || quickItem.barcode}`);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(`Error al añadir artículo`);
    }
  };

  const getItemPrice = (item) => {
    // Para artículos genéricos, usar rental_price
    if (item.is_generic && item.rental_price) {
      return item.rental_price;
    }
    
    if (item.customPrice !== null && item.customPrice !== undefined) {
      return item.customPrice;
    }
    
    const tariff = tariffs.find(t => t.item_type === item.item_type);
    if (!tariff) {
      // Sin tarifa configurada - retornar 0 (se mostrará alerta en UI)
      return 0;
    }

    // Respect the item's own itemDays if set (individual artículo ampliado/reducido
    // por separado del alquiler global). Fall back to the global numDays.
    const effectiveDays =
      typeof item.itemDays === "number" && item.itemDays > 0
        ? item.itemDays
        : numDays;

    // Usar exclusivamente precios diarios (day_1 a day_10, luego day_11_plus)
    if (effectiveDays <= 10) {
      const dayPrice = tariff[`day_${effectiveDays}`];
      if (dayPrice !== null && dayPrice !== undefined && dayPrice > 0) {
        return dayPrice;
      }
    } else if (tariff.day_11_plus !== null && tariff.day_11_plus !== undefined && tariff.day_11_plus > 0) {
      return tariff.day_11_plus;
    }
    
    // Sin precio configurado para estos días - retornar 0
    return 0;
  };

  // ─── PRICE AT ADD TIME ──────────────────────────────────────────────────────
  // (defined earlier, before the scanner hooks - see line ~369)


  // Verifica si un artículo tiene tarifa configurada
  const itemHasTariff = (item) => {
    if (item.is_generic && item.rental_price) return true;
    if (item.customPrice !== null && item.customPrice !== undefined) return true;
    const tariff = tariffs.find(t => t.item_type === item.item_type);
    if (!tariff) return false;
    // Verificar que tenga al menos un precio diario
    for (let i = 1; i <= 10; i++) {
      if (tariff[`day_${i}`] > 0) return true;
    }
    return tariff.day_11_plus > 0;
  };

  // Group items by detected packs for unified display
  // PASO 1: Consolidación - Fusionar códigos de hijos en el nombre del padre
  // PASO 2: Filtrado - Solo devolver packs (padres) y items sueltos, NUNCA hijos
  const getGroupedCartItems = () => {
    if (detectedPacks.length === 0) {
      // No packs - return items as individual entries
      return items.map(item => ({
        type: 'single',
        item: item,
        items: [item],
        price: item.customPrice !== null ? item.customPrice : getItemPriceFromTariff(item),
        days: item.itemDays || numDays
      }));
    }

    // PASO 2: Create a set of item IDs that are HIJOS (components of packs)
    // These will be EXCLUDED from the visual list
    // CRITICAL: Add BOTH barcode AND id to ensure filtering works
    const packItemIds = new Set();
    detectedPacks.forEach(dp => {
      // Add all barcodes from the detected pack
      dp.items.forEach(itemBarcode => packItemIds.add(itemBarcode));
      
      // Also add the actual item IDs from the items array
      items.forEach(item => {
        if (dp.items.includes(item.barcode) || dp.items.includes(item.id)) {
          packItemIds.add(item.id);
          packItemIds.add(item.barcode);
        }
      });
    });

    const groups = [];

    // Add detected packs as CONSOLIDATED groups (PADRES)
    // Each detected pack has a unique instanceId for multi-pack support
    detectedPacks.forEach((dp) => {
      // Get the actual item objects for THIS SPECIFIC pack instance
      const packItemObjects = items.filter(item => 
        dp.items.includes(item.barcode) || dp.items.includes(item.id)
      );
      
      // Use the first item's days as the pack days
      const packDays = packItemObjects[0]?.itemDays || numDays;
      
      // CRITICAL: Get pack price - check for custom price first
      const firstPackItem = packItemObjects[0];
      const customPackPrice = firstPackItem?.customPackPrice;
      const basePackPrice = getPackPrice(dp.pack, packDays);
      const packPrice = customPackPrice !== null && customPackPrice !== undefined 
        ? customPackPrice 
        : basePackPrice;
      const isPackPriceEdited = customPackPrice !== null && customPackPrice !== undefined;
      
      // Get custom pack name if set by user
      const customPackName = firstPackItem?.customPackName;
      
      // PASO 1: CONSOLIDACIÓN - Extraer códigos de los HIJOS y fusionarlos en el nombre
      const childCodes = packItemObjects.map(item => 
        item.internal_code || item.barcode?.substring(0, 10) || 'N/A'
      ).join(' / ');
      
      // Crear nombre fusionado: Use custom name if set, otherwise use original pack name
      // "Pack Gama Media (SKI-001 / BOT-204)" OR "Pack Personalizado (SKI-001 / BOT-204)"
      const displayPackName = customPackName || dp.pack.name;
      const fusedName = `${displayPackName} (${childCodes})`;
      
      groups.push({
        type: 'pack',
        pack: dp.pack,
        fusedName: fusedName,  // Nombre con códigos de hijos incrustados
        displayName: displayPackName,  // Name without codes (for editing)
        childCodes: childCodes,
        items: packItemObjects,  // Solo para referencia interna, NO para renderizar filas
        price: packPrice,  // Precio TOTAL del pack (puede ser personalizado)
        basePrice: basePackPrice,  // Precio original sin editar
        isEdited: isPackPriceEdited,  // Flag para mostrar "EDITADO"
        days: packDays,
        packId: dp.instanceId || `pack-${Date.now()}`  // Use unique instance ID
      });
    });

    // PASO 2: Add ONLY items that are NOT part of any pack (items sueltos)
    // CONDICIÓN DE BLOQUEO: Si el item es componente de pack, NO renderizar
    items.forEach(item => {
      const itemId = item.id || item.barcode;
      const itemBarcode = item.barcode;
      
      // BLOQUEO: Si el item es HIJO de un pack, SALTARSE (no añadir)
      if (packItemIds.has(itemId) || packItemIds.has(itemBarcode)) {
        return; // CONTINUE - No generar entrada para este item
      }
      
      // Solo llegamos aquí si el item NO es parte de ningún pack
      const basePrice = getItemPriceFromTariff(item);
      const customPrice = item.customPrice;
      const finalPrice = customPrice !== null && customPrice !== undefined ? customPrice : basePrice;
      const isEdited = customPrice !== null && customPrice !== undefined;
      
      groups.push({
        type: 'single',
        item: item,
        items: [item],
        price: finalPrice,
        basePrice: basePrice,
        isEdited: isEdited,
        days: item.itemDays || numDays
      });
    });

    return groups;
  };

  // Get TOTAL price from tariff for an item (LOOK-UP escalonado, NO multiplicación)
  const getItemPriceFromTariff = (item) => {
    // Si tiene precio personalizado, usarlo
    if (item.customPrice !== null && item.customPrice !== undefined) return item.customPrice;
    
    // Buscar tarifa del tipo de artículo
    const tariff = tariffs.find(t => t.item_type === item.item_type);
    if (!tariff) return item.rental_price || 0;
    
    // LOOK-UP: Buscar el precio TOTAL para X días (NO es precio/día)
    const days = item.itemDays || numDays;
    const dayField = days <= 10 ? `day_${days}` : 'day_11_plus';
    return tariff[dayField] || tariff.day_1 || 0;
  };

  // Handle item type change - automatically update price from tariff
  const handleItemTypeChange = (cartKey, newType) => {
    setItems(prev => prev.map(item => {
      if (getCartKey(item) === cartKey) {
        return {
          ...item,
          item_type: newType,
          customPrice: null
        };
      }
      return item;
    }));
    setEditingItemType(null);
    toast.success("Tipo actualizado - Precio ajustado automáticamente");
  };

  // Handle PACK DEFINITION CHANGE - Switch to a different pack type
  // REGLA: Actualiza la línea EXISTENTE directamente. No crea duplicados.
  // El nombre y el precio cambian al del nuevo pack. Los artículos permanecen intactos.
  const handlePackDefinitionChange = (currentPackItems, newPackId, instanceId) => {
    // Find the new pack definition
    const newPack = packs.find(p => p._id === newPackId || p.id === newPackId);
    if (!newPack) {
      toast.error("Pack no encontrado");
      return;
    }
    
    // DIRECT UPDATE: Find the specific pack group by instanceId and replace only the pack definition.
    // No re-detection, no useEffect trigger. Items stay exactly the same.
    setDetectedPacks(prev => prev.map(dp => {
      const dpId = dp.instanceId;
      if (dpId === instanceId) {
        return {
          ...dp,
          pack: newPack,
          manualOverride: true  // Protege este grupo del useEffect de auto-detección
        };
      }
      return dp;
    }));
    
    // Clear any custom pack price on these items so the new pack's price applies
    const packBarcodes = new Set(currentPackItems.map(i => i.barcode));
    setItems(prev => prev.map(item =>
      packBarcodes.has(item.barcode)
        ? { ...item, customPackPrice: null, manualPriceEdit: false }
        : item
    ));
    
    const packDays = currentPackItems[0]?.itemDays || numDays;
    const newPrice = getPackPrice(newPack, packDays);
    toast.success(`Pack cambiado a "${newPack.name}" - ${formatCurrency(newPrice)}`);
  };

  // Handle COMPONENT TYPE TEXT CHANGE - Custom label for this rental ONLY
  // This does NOT update the database - it's a LOCAL display override
  const handleComponentTypeLabelChange = (itemBarcode, newLabel) => {
    setItems(prev => prev.map(item => {
      if (item.barcode === itemBarcode) {
        return {
          ...item,
          customTypeLabel: newLabel
        };
      }
      return item;
    }));
    toast.success("Etiqueta personalizada guardada");
  };

  // Update days for all items in a pack - RECALCULATE pack price using scaled tariff
  const updatePackDays = (packItems, newDays, keepCustomPrice = false) => {
    // Validate: only positive integers allowed
    const days = parseInt(newDays);
    if (isNaN(days) || days < 1) {
      toast.error("Los días del pack deben ser un número positivo");
      setEditingItemDays(null);
      return;
    }
    
    const packItemIds = new Set(packItems.map(i => i.id || i.barcode));
    
    // Get the first item to check if price was manually edited
    const firstItem = packItems[0];
    const wasManuallyEdited = firstItem?.manualPriceEdit === true;
    
    setItems(prev => prev.map(item => {
      const itemId = item.id || item.barcode;
      if (packItemIds.has(itemId)) {
        const isFirstItem = (item.id || item.barcode) === (firstItem?.id || firstItem?.barcode);
        
        // CRITICAL: Only clear customPackPrice if:
        // 1. It wasn't manually edited by user (not keepCustomPrice)
        // 2. OR if explicitly told to recalculate (!keepCustomPrice)
        // This ensures the price is recalculated from the tariff table
        const updatedItem = { 
          ...item, 
          itemDays: days, 
          manualDaysEdit: true 
        };
        
        // Clear customPackPrice on first item so getGroupedCartItems will recalculate
        // ONLY if the price was NOT manually edited
        if (isFirstItem && !wasManuallyEdited && !keepCustomPrice) {
          updatedItem.customPackPrice = null;
          updatedItem.manualPriceEdit = false;
        }
        
        return updatedItem;
      }
      return item;
    }));
    
    setEditingItemDays(null);
    
    // Provide feedback about the recalculation
    if (!wasManuallyEdited) {
      toast.success(`Duración del pack actualizada a ${days} día${days !== 1 ? 's' : ''} - Precio recalculado`);
    } else {
      toast.info(`Duración del pack actualizada a ${days} día${days !== 1 ? 's' : ''} - Precio manual mantenido`);
    }
  };

  // ─── REACTIVE SUBTOTAL (useMemo) ─────────────────────────────────────────────
  // Self-contained computation: derives price directly from items, detected packs,
  // and tariffs. No intermediate callbacks or stale closures — purely reactive.
  const subtotal = useMemo(() => {
    if (!items || items.length === 0) return 0;

    // 1. Identify which items are inside detected packs
    const itemsInPacksSet = new Set();
    detectedPacks.forEach(dp => {
      dp.items.forEach(barcode => itemsInPacksSet.add(barcode));
      items.forEach(item => {
        if (dp.items.includes(item.barcode) || dp.items.includes(item.id)) {
          itemsInPacksSet.add(item.id);
          itemsInPacksSet.add(item.barcode);
        }
      });
    });

    // 2. Sum pack prices
    let packsTotal = 0;
    detectedPacks.forEach(dp => {
      const packItems = items.filter(item =>
        dp.items.includes(item.barcode) || dp.items.includes(item.id)
      );
      const packDays = packItems[0]?.itemDays || numDays;
      const customPackPrice = packItems[0]?.customPackPrice;

      let packPrice;
      if (customPackPrice !== null && customPackPrice !== undefined) {
        packPrice = customPackPrice;
      } else {
        const pack = dp.pack;
        if (packDays <= 10 && pack[`day_${packDays}`]) {
          packPrice = pack[`day_${packDays}`];
        } else if (packDays > 10 && pack.day_11_plus) {
          packPrice = pack.day_11_plus;
        } else {
          packPrice = pack.day_1 || 0;
        }
      }
      packsTotal += packPrice;
    });

    // 3. Sum individual item prices (items NOT in any pack)
    // PRIORITY ORDER must match getItemPriceFromTariff():
    //   1. customPrice (user override)
    //   2. Tariff lookup (primary source of truth)
    //   3. rental_price (fallback only when no tariff exists)
    let individualsTotal = 0;
    items.forEach(item => {
      if (itemsInPacksSet.has(item.barcode) || itemsInPacksSet.has(item.id)) return;

      const itemDays = item.itemDays || numDays;
      let itemPrice = 0;

      if (item.customPrice !== null && item.customPrice !== undefined) {
        itemPrice = item.customPrice;
      } else {
        const tariff = tariffs.find(t => t.item_type === item.item_type);
        if (!tariff) {
          itemPrice = item.rental_price || 0;
        } else {
          const dayField = itemDays <= 10 ? `day_${itemDays}` : 'day_11_plus';
          itemPrice = tariff[dayField] || tariff.day_1 || 0;
        }
      }
      individualsTotal += itemPrice;
    });

    return packsTotal + individualsTotal;
  }, [items, numDays, detectedPacks, tariffs]);

  const total = useMemo(() => {
    let t = subtotal;
    const providerDiscount = customer?.source
      ? (sources.find(s => s.name === customer.source)?.discount_percent || 0)
      : 0;
    if (providerDiscount > 0) t = t - (t * providerDiscount / 100);
    if (discountType === 'percent' && discountValue) t = t - (t * parseFloat(discountValue) / 100);
    if (discountType === 'fixed' && discountValue) t = t - parseFloat(discountValue);
    return Math.max(0, t);
  }, [subtotal, discountType, discountValue, customer, sources]);

  const getProviderDiscount = () => {
    if (!customer?.source) return 0;
    const provider = sources.find(s => s.name === customer.source);
    return provider?.discount_percent || 0;
  };

  // Actualizar precio personalizado de un item individual
  const updateItemPrice = (cartKey, newPrice) => {
    const price = parseFloat(newPrice);
    if (isNaN(price) || price < 0) {
      toast.error("El precio debe ser un número positivo o cero");
      setEditingItemPrice(null);
      return;
    }
    setItems(prev => prev.map(item => {
      if (getCartKey(item) === cartKey) {
        // Keep calculatedPrice in sync with the custom price
        return { ...item, customPrice: price, calculatedPrice: price };
      }
      return item;
    }));
    setEditingItemPrice(null);
  };

  // Actualizar precio de un pack completo (se guarda en el primer item del pack)
  // Marca manualPriceEdit = true para que updatePackDays no resetee el precio
  const updatePackPrice = (packItems, newPrice) => {
    // Validate: only non-negative numbers allowed
    const price = parseFloat(newPrice);
    if (isNaN(price) || price < 0) {
      toast.error("El precio del pack debe ser un número positivo o cero");
      setEditingPackPrice(null);
      return;
    }
    const packItemIds = new Set(packItems.map(i => i.id || i.barcode));
    
    // Guardamos el precio del pack en el primer item como customPackPrice
    // Y marcamos manualPriceEdit = true para preservar en cambios de días
    setItems(prev => {
      const firstPackIdx = prev.findIndex(i => packItemIds.has(i.id || i.barcode));
      return prev.map((item, idx) => {
        const itemId = item.id || item.barcode;
        if (packItemIds.has(itemId)) {
          // Solo el primer item del pack guarda el precio personalizado
          if (idx === firstPackIdx) {
            return { 
              ...item, 
              customPackPrice: isNaN(price) ? null : price,
              manualPriceEdit: true  // Mark as manually edited to preserve on days change
            };
          }
        }
        return item;
      });
    });
    
    setEditingPackPrice(null);
    toast.success("Precio del pack actualizado manualmente");
  };

  // Resetear precio del pack a la tarifa original (elimina edición manual)
  const resetPackPrice = (packItems) => {
    const packItemIds = new Set(packItems.map(i => i.id || i.barcode));
    
    setItems(prev => {
      const firstPackIdx = prev.findIndex(i => packItemIds.has(i.id || i.barcode));
      return prev.map((item, idx) => {
        const itemId = item.id || item.barcode;
        if (packItemIds.has(itemId)) {
          if (idx === firstPackIdx) {
            // Clear custom price and manual edit flag
            return { 
              ...item, 
              customPackPrice: null,
              manualPriceEdit: false
            };
          }
        }
        return item;
      });
    });
    
    toast.success("Precio del pack restaurado a tarifa");
  };

  // calculateTotal kept for compatibility but now delegates to useMemo `total`
  const calculateTotal = () => total;
  
  // NUEVO: Calcular el total A COBRAR HOY (incluye depósito)
  const calculateTotalToPay = () => {
    const depositAmount = Number(parseFloat(deposit) || 0);
    return total + depositAmount;
  };

  const completeRental = async () => {
    if (!customer) {
      toast.error("Selecciona un cliente");
      return;
    }
    if (items.length === 0) {
      toast.error("Añade al menos un artículo");
      return;
    }
    // Cobro Online Cotejado — if the customer already paid on the web,
    // prefill the "importe abonado" field with the remaining due so the
    // clerk only takes the pending amount (or 0 if fully paid online).
    const prepaidOnline = Number(onlineBooking?.amount) || 0;
    if (prepaidOnline > 0 && !paidAmount) {
      const depositAmount = Number(parseFloat(deposit) || 0);
      const remaining = Math.max(0, total + depositAmount - prepaidOnline);
      setPaidAmount(remaining.toFixed(2));
    }
    setShowPaymentDialog(true);
  };

  const processPaymentAndCompleteRental = async () => {
    const selectedMode = mixedPaymentState?.mode || "cash";

    if (!selectedMode) {
      toast.error("Selecciona un método de pago");
      return;
    }

    setProcessingPayment(true);
    
    const API = process.env.REACT_APP_BACKEND_URL;
    
    // Derive amounts from mixedPaymentState
    const cleanTotal = Number(total.toFixed(2));
    const cleanDeposit = Number(parseFloat(deposit) || 0);
    let cleanPaidAmount;

    if (selectedMode === "pending") {
      cleanPaidAmount = 0;
    } else if (selectedMode === "online" || selectedMode === "pago_online") {
      cleanPaidAmount = cleanTotal;
    } else {
      cleanPaidAmount = Number(((mixedPaymentState?.cashAmount || 0) + (mixedPaymentState?.cardAmount || 0)).toFixed(2));
      // Cap to total (cannot overpay intentionally in the record)
      cleanPaidAmount = Math.min(cleanPaidAmount, cleanTotal);
    }

    // payment_method and payment_detail
    const finalMethod = selectedMode === "pending" ? "pending"
      : selectedMode === "online" ? "pago_online"
      : mixedPaymentState?.method || "cash";

    const paymentDetail = finalMethod === "mixed"
      ? { cash: mixedPaymentState?.cashAmount || 0, card: mixedPaymentState?.cardAmount || 0 }
      : null;

    // Back-compat: paymentMethodSelected for legacy references
    const paymentMethodSelected = finalMethod;
    
    // === VERIFICACIÓN DE CAJA ===
    // SOLO verificar si hay un pago > 0 (sin importar el método)
    if (cleanPaidAmount > 0 || cleanDeposit > 0) {
      try {
        const sessionCheck = await fetch(`${API}/api/cash/sessions/active`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (!sessionCheck.ok) {
          toast.error("⚠️ La caja debe estar abierta antes de procesar cobros. Ábrela desde el módulo de CAJA.");
          setProcessingPayment(false);
          return;
        }
        
        const sessionText = await sessionCheck.text();
        if (!sessionText || sessionText.trim().startsWith('<') || !JSON.parse(sessionText)?.id) {
          toast.error("⚠️ La caja debe estar abierta antes de procesar cobros. Ábrela desde el módulo de CAJA.");
          setProcessingPayment(false);
          return;
        }
      } catch (e) {
        // Error al verificar la caja - BLOQUEAR por seguridad
        console.error("Error verificando sesión de caja:", e);
        toast.error("⚠️ No se puede verificar el estado de la caja. Por favor, verifica que la caja esté abierta.");
        setProcessingPayment(false);
        return;
      }
    }
    
    // === ENVÍO DEL ALQUILER ===
    try {
      // Calcular pendiente
      const pendingAmount = cleanTotal - cleanPaidAmount;
      
      // Lo que entra en caja HOY = Importe pagado + Depósito
      const cashInToday = cleanPaidAmount + cleanDeposit;
      
      console.log('[Rental] Resumen financiero:', {
        total: cleanTotal,
        paidAmount: cleanPaidAmount,
        deposit: cleanDeposit,
        pending: pendingAmount,
        cashInToday: cashInToday
      });
      
      // Build a map: barcode → pack_instance_id (from detectedPacks)
      const barcodeToPackId = {};
      detectedPacks.forEach(dp => {
        dp.items.forEach(barcode => {
          barcodeToPackId[barcode] = dp.instanceId;
        });
        items.forEach(item => {
          if (dp.items.includes(item.barcode) || dp.items.includes(item.id)) {
            barcodeToPackId[item.barcode] = dp.instanceId;
            barcodeToPackId[item.id] = dp.instanceId;
          }
        });
      });

      const itemsToSend = items.map(i => {
        // Find which pack group this item belongs to (if any)
        const myPackInstanceId = barcodeToPackId[i.barcode] || barcodeToPackId[i.id] || null;
        let packId = null;
        let packName = null;
        if (myPackInstanceId) {
          // Find the pack definition for this instance
          const detectedPack = detectedPacks.find(dp =>
            dp.instanceId === myPackInstanceId ||
            dp.items?.includes(i.barcode) ||
            dp.items?.includes(i.id)
          );
          if (detectedPack?.pack) {
            packId = detectedPack.pack._id || detectedPack.pack.id || null;
            packName = detectedPack.pack.name || null;
          }
        }
        return {
          barcode: String(i.barcode || i.id || ''),
          person_name: "",
          is_generic: i.is_generic || false,
          quantity: 1,
          unit_price: Number(i.rental_price || getItemPriceWithPack(i) || 0),
          item_days: i.itemDays || numDays,
          pack_instance_id: myPackInstanceId,
          pack_id: packId,
          pack_name: packName,
          custom_type_label: i.customTypeLabel || null
        };
      });
      
      const rentalResponse = await fetch(`${API}/api/rentals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          customer_id: customer?.id || null,
          start_date: startDate,
          end_date: endDate,
          items: itemsToSend,
          payment_method: paymentMethodSelected || 'cash',
          payment_detail: paymentDetail,
          total_amount: cleanTotal,
          paid_amount: Number(cleanPaidAmount.toFixed(2)),
          deposit: cleanDeposit,
          notes: notes || ''
        })
      });
      
      // Manejo robusto de respuesta
      if (!rentalResponse.ok) {
        // Leer como JSON directamente para errores
        try {
          const errorData = await rentalResponse.json();
          console.error('[Rental] Server error response:', rentalResponse.status, errorData);
          toast.error(errorData.detail || `Error al crear alquiler (${rentalResponse.status})`);
        } catch (parseErr) {
          // Si no es JSON válido, mostrar error con status code
          const textBody = await rentalResponse.text().catch(() => '');
          console.error('[Rental] Non-JSON error response:', rentalResponse.status, textBody);
          toast.error(`Error al crear alquiler (${rentalResponse.status})`);
        }
        setProcessingPayment(false);
        return;
      }
      
      // Respuesta exitosa - leer como JSON
      let rentalData;
      try {
        rentalData = await rentalResponse.json();
      } catch (e) {
        console.error('[Rental] Error parseando respuesta:', e);
        toast.error('Error al procesar la respuesta del servidor');
        setProcessingPayment(false);
        return;
      }
      
      // Calcular pendiente para mostrar en UI
      const pendingToShow = cleanTotal - cleanPaidAmount;
      
      // === ÉXITO ===
      const totalToPay = cleanTotal + cleanDeposit; // Total que el cliente debe pagar HOY
      const cashGivenAmount = Number(parseFloat(cashGiven) || 0);

      // If this rental was from an online booking, fulfill it now
      if (bookingId && rentalData?.id) {
        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/online-bookings/${bookingId}/fulfill`,
            { rental_id: rentalData.id },
            { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
          );
        } catch (e) {
          console.warn("[OnlineBooking] Could not fulfill booking:", e.message);
        }
      }
      
      setCompletedRental({
        ...rentalData,
        customer_name: customer?.name || 'Cliente',
        customer_dni: customer?.dni || '',
        items_detail: items,
        total_amount: cleanTotal,
        paid_amount: cleanPaidAmount,
        pending_amount: pendingToShow,
        deposit: cleanDeposit,
        payment_detail: paymentDetail,
        change: paymentMethodSelected === "cash" && cashGivenAmount > totalToPay
          ? Number((cashGivenAmount - totalToPay).toFixed(2)) 
          : 0
      });
      
      setShowPaymentDialog(false);
      setCashGiven("");
      setShowSuccessDialog(true);
      
      // Mensaje según estado
      if (pendingToShow > 0) {
        toast.success(`✅ Alquiler guardado. Pendiente: ${formatCurrency(pendingToShow)}`);
      } else {
        toast.success("✅ Alquiler completado - Pagado en su totalidad");
      }
      
    } catch (error) {
      console.error("[Rental] Error creando alquiler:", error);
      console.error("[Rental] Full error object:", error);
      console.error("[Rental] Error message:", error.message);
      console.error("[Rental] Error stack:", error.stack);
      setProcessingPayment(false);
      
      // Mostrar el error real - fetch errors tienen .message, no .response
      const errorMessage = error.message || "Error desconocido al crear el alquiler";
      toast.error(`❌ ${errorMessage}`);
      
      // No redirigir - dejar al usuario en el formulario para que pueda corregir
      return;
    } finally {
      setProcessingPayment(false);
    }
  };

  // Función vacía - Ya no se usa apertura automática
  const openCashAndContinue = async () => {
    toast.info("Por favor, abra la caja desde el módulo de CAJA.");
    setShowAutoOpenCashDialog(false);
  };

  const printRentalTicket = () => {
    if (!completedRental) return;
    
    // Helper to get item type label
    const getTypeLabel = (item) => {
      // Priority: customTypeLabel > itemTypes lookup > item_type
      if (item.customTypeLabel || item.custom_type_label) {
        return item.customTypeLabel || item.custom_type_label;
      }
      const type = itemTypes.find(t => t.value === item.item_type);
      return type?.label || item.item_type || 'Artículo';
    };
    
    // Prepare items for the ticket
    const ticketItems = [];
    
    // Group items by pack
    const packItems = {};
    const standaloneItems = [];
    
    // Merge items with their custom labels from the cart
    const itemsWithLabels = (completedRental.items_detail || []).map(item => {
      // Find the original cart item to get customTypeLabel
      const cartItem = items.find(i => i.barcode === item.barcode);
      return {
        ...item,
        customTypeLabel: cartItem?.customTypeLabel || item.custom_type_label || null
      };
    });
    
    itemsWithLabels.forEach(item => {
      const inPack = detectedPacks.find(dp => dp.items.includes(item.barcode));
      if (inPack) {
        const packId = inPack.pack.id;
        if (!packItems[packId]) {
          packItems[packId] = { pack: inPack.pack, items: [], days: item.itemDays || numDays };
        }
        packItems[packId].items.push(item);
      } else {
        standaloneItems.push(item);
      }
    });
    
    // Add standalone items
    standaloneItems.forEach(item => {
      const typeLabel = getTypeLabel(item);  // Now uses custom label if available
      const days = item.itemDays || numDays;
      const tariff = tariffs.find(t => t.item_type === item.item_type);
      const dayField = days <= 10 ? `day_${days}` : 'day_11_plus';
      const totalPrice = item.customPrice || item.custom_price || (tariff ? tariff[dayField] : 0) || 0;
      
      const modelStr = `${item.brand || ''} ${item.model || ''}`.trim();
      
      ticketItems.push({
        name: `${typeLabel} ${modelStr}`.trim(),
        size: item.size || '',
        internal_code: item.internal_code || '',
        item_type: item.item_type,
        days: days,
        price: totalPrice,
        subtotal: totalPrice,
        startDate: startDate || null,
        endDate: endDate || null
      });
    });
    
    // Add pack items - show individual components with their custom labels
    Object.values(packItems).forEach(packData => {
      const packDays = packData.days;
      const packTotal = getPackPrice(packData.pack, packDays);
      
      // Build component list with custom labels for the ticket
      const componentDescriptions = packData.items.map(item => {
        const label = getTypeLabel(item);
        const code = item.internal_code || item.barcode?.substring(0, 10) || '';
        return `${label}${code ? ` (${code})` : ''}`;
      }).join(' + ');
      
      ticketItems.push({
        name: `${packData.pack.name}`,
        internal_code: componentDescriptions,  // Show custom labels in description
        item_type: 'pack',
        days: packDays,
        price: packTotal,
        subtotal: packTotal,
        startDate: startDate || null,
        endDate: endDate || null
      });
    });

    // Imprimir ticket de alquiler — sólo si el operador activó auto-print
    // en Configuración > Hardware. Si está OFF hay que dejarle un botón
    // manual en el diálogo de éxito (ya existe: renderTicketButton).
    if (settings.autoPrint !== false) {
      printTicket({
        ticketType: 'rental',
        data: {
          operationNumber: completedRental.operation_number,
          date: new Date().toISOString(),
          customer: customer?.name || completedRental.customer_name || '',
          dni: customer?.dni || completedRental.customer_dni || '',
          startDate: startDate,
          endDate: endDate,
          days: numDays,
          items: ticketItems,
          total: completedRental.total_amount || 0,
          paymentMethod: completedRental.payment_method || paymentMethod,
          paymentDetail: completedRental.payment_detail || null,
          deposit: completedRental.deposit || 0,
          pendingAmount: completedRental.pending_amount || 0
        },
        settings: {
          companyLogo: settings.companyLogo,
          ticketHeader: settings.ticketHeader,
          ticketFooter: settings.ticketFooter,
          ticketTerms: settings.ticketTerms,
          showDniOnTicket: settings.showDniOnTicket,
          showVatOnTicket: settings.showVatOnTicket,
          defaultVat: settings.defaultVat,
          vatIncludedInPrices: settings.vatIncludedInPrices,
          language: settings.language,
          paperWidth: settings.paperWidth || '80mm'
        }
      }).catch(err => {
        console.error('[Print] Error al imprimir ticket de alquiler:', err);
      });
    }
  };

  const closeSuccessDialog = () => {
    setShowSuccessDialog(false);
    setCompletedRental(null);
    
    // Cerrar el ticket actual y pasar al siguiente
    completeAndCloseActiveTicket();
    
    // Limpiar estados transitorios adicionales
    setSearchTerm("");
    setPaidAmount("");
    setDeposit("");
    
    if (searchRef.current) searchRef.current.focus();
  };

  const resetForm = () => {
    // Usar clearCart() para limpiar el estado persistido del ticket actual
    clearCart();
    
    // Limpiar estados transitorios adicionales
    setSearchTerm("");
    setPaidAmount("");
    setDeposit("");
    setDetectedPacks([]);
    
    if (searchRef.current) searchRef.current.focus();
  };

  // Función para vaciar carrito manualmente (botón "Vaciar Carrito")
  const handleClearCart = () => {
    if (items.length === 0 && !customer) {
      toast.info("El carrito ya está vacío");
      return;
    }
    
    clearCart();
    setSearchTerm("");
    setPaidAmount("");
    setDeposit("");
    toast.success("Carrito vaciado");
    
    if (barcodeRef.current) barcodeRef.current.focus();
  };

  // subtotal and total are now useMemo values declared above (reactive)
  const hasDiscount = total < subtotal;

  return (
    <div 
      ref={mainContainerRef}
      className="h-screen flex flex-col bg-slate-50 overflow-hidden" 
      data-testid="new-rental-page"
    >
      {/* Online Booking Banner */}
      {onlineBooking && (
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 flex items-center justify-between gap-4 flex-shrink-0" data-testid="online-booking-banner">
          <div className="flex items-center gap-3 min-w-0 flex-wrap">
            <Globe className="h-4 w-4 flex-shrink-0" />
            <span className="font-bold text-sm truncate">
              Reserva online: <span className="font-mono">{onlineBooking.localizador}</span>
              {" · "}{onlineBooking.client_name}
              {" · "}{onlineBooking.start_date} → {onlineBooking.end_date}
            </span>
            <span className="text-xs text-white/70 hidden md:block">
              [{onlineBooking.categories.join(", ")}]
            </span>
            {Number(onlineBooking.amount) > 0 && (
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-500/25 border border-emerald-300/40"
                data-testid="booking-prepaid-amount"
              >
                Pagado online: {formatCurrency(Number(onlineBooking.amount))}
              </span>
            )}
            {Number(onlineBooking.deposit) > 0 && (
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/25 border border-amber-300/40"
                data-testid="booking-prepaid-deposit"
              >
                Depósito web: {formatCurrency(Number(onlineBooking.deposit))}
              </span>
            )}
          </div>
          <button
            onClick={() => setOnlineBooking(null)}
            className="text-white/70 hover:text-white flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {/* Header con Tabs de Tickets */}
      <div className="bg-white border-b border-slate-200 flex-shrink-0">
        {/* Título */}
        <div className="px-6 py-2 border-b border-slate-100">
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Nuevo Alquiler
          </h1>
        </div>
        
        {/* Barra de Pestañas de Tickets */}
        <div className="px-4 py-2 flex items-center gap-1 overflow-x-auto" data-testid="ticket-tabs">
          {tickets.map((ticket, index) => {
            const isActive = ticket.id === activeTicketId;
            const displayName = getTicketDisplayName(ticket, index);
            const hasItems = ticket.data.items.length > 0;
            const hasCustomer = ticket.data.customer !== null;
            
            return (
              <div
                key={ticket.id}
                className={`
                  group flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer
                  transition-all duration-150 min-w-0 max-w-[180px]
                  ${isActive 
                    ? 'bg-primary text-white shadow-md' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }
                `}
                onClick={() => switchToTicket(ticket.id)}
                data-testid={`ticket-tab-${index}`}
              >
                {/* Indicador de contenido */}
                {(hasItems || hasCustomer) && !isActive && (
                  <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                )}
                
                {/* Nombre del ticket */}
                <span className={`text-sm font-medium truncate ${isActive ? '' : ''}`}>
                  {displayName}
                </span>
                
                {/* Badge de artículos */}
                {hasItems && (
                  <Badge 
                    variant={isActive ? "secondary" : "outline"} 
                    className={`text-xs px-1.5 py-0 h-5 flex-shrink-0 ${isActive ? 'bg-white/20 text-white border-0' : ''}`}
                  >
                    {ticket.data.items.length}
                  </Badge>
                )}
                
                {/* Botón cerrar (solo si hay más de 1 ticket) */}
                {tickets.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      closeTicket(ticket.id);
                    }}
                    className={`
                      ml-1 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity
                      ${isActive 
                        ? 'hover:bg-white/20 text-white' 
                        : 'hover:bg-slate-300 text-slate-500 hover:text-slate-700'
                      }
                    `}
                    data-testid={`close-ticket-${index}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
          
          {/* Botón añadir nuevo ticket */}
          <Button
            variant="ghost"
            size="sm"
            onClick={createTicket}
            className="h-8 w-8 p-0 rounded-lg hover:bg-slate-200 flex-shrink-0"
            data-testid="add-ticket-btn"
          >
            <Plus className="h-4 w-4 text-slate-600" />
          </Button>
          
          {/* Indicador de tickets abiertos */}
          {tickets.length > 1 && (
            <span className="text-xs text-slate-600 ml-2 flex-shrink-0">
              {tickets.length} tickets abiertos
            </span>
          )}
        </div>
      </div>

      {/* Main Content - Todo el ancho */}
      <div className="flex-1 overflow-y-auto px-6 py-4 pb-28 space-y-3">
        
        {/* FILA 1: Cliente + Duración + Artículos en 3 columnas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          
          {/* Cliente */}
          <Card className="border-slate-200">
            <CardHeader className="py-2 px-4">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4 text-slate-500" />
                Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 px-4 pb-3">
              {/* Smart Autocomplete Search */}
              <div className="relative">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      ref={(el) => {
                        searchRef.current = el;
                        customerSearchRef.current = el;
                      }}
                      placeholder="Busca por nombre o DNI (mínimo 2 caracteres)..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        // Tab navigation
                        if (e.key === 'Tab' && !isEditingCartItem && !showSuggestions) {
                          e.preventDefault();
                          if (e.shiftKey) {
                            focusPrevField('customer');
                          } else {
                            focusNextField('customer');
                          }
                          return;
                        }
                        // Normal handling (suggestions navigation)
                        handleKeyDown(e);
                      }}
                      onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
                      tabIndex={2}
                      className="h-9 pr-10 text-sm"
                      data-testid="customer-search-input"
                      autoFocus
                      disabled={!!customer}
                      autoComplete="off"
                    />
                    {searchTerm && !customer && (
                      <button
                        onClick={() => {
                          setSearchTerm("");
                          setShowSuggestions(false);
                          searchRef.current?.focus();
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-600"
                        tabIndex={-1}
                      >
                        <X className="h-4 w-4" tabIndex={-1} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                  {!customer && (
                    <Button 
                      onClick={searchCustomer} 
                      disabled={searchLoading}
                      className="h-9 px-3"
                      data-testid="customer-search-btn"
                      tabIndex={-1}
                    >
                      {searchLoading ? <Loader2 className="h-4 w-4 animate-spin" tabIndex={-1} aria-hidden="true" /> : <Search className="h-4 w-4" tabIndex={-1} aria-hidden="true" />}
                    </Button>
                  )}
                </div>

                {/* Suggestions Dropdown */}
                {showSuggestions && customerSuggestions.length > 0 && !customer && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                    <div className="p-2">
                      <p className="text-xs text-slate-500 px-2 py-1 mb-1" tabIndex={-1}>
                        {customerSuggestions.length} cliente(s) encontrado(s)
                      </p>
                      {customerSuggestions.map((suggestion, index) => (
                        <button
                          key={suggestion.id}
                          onClick={() => selectCustomer(suggestion)}
                          tabIndex={-1}
                          className={`w-full text-left px-3 py-2.5 rounded-md transition-colors ${
                            index === selectedIndex 
                              ? 'bg-primary text-white' 
                              : 'hover:bg-slate-50'
                          }`}
                          data-testid={`suggestion-${index}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className={`font-semibold truncate ${
                                index === selectedIndex ? 'text-white' : 'text-slate-900'
                              }`}>
                                {suggestion.name}
                              </p>
                              <p className={`text-sm font-mono ${
                                index === selectedIndex ? 'text-white/90' : 'text-slate-500'
                              }`}>
                                {suggestion.dni}
                              </p>
                              {suggestion.phone && (
                                <p className={`text-xs ${
                                  index === selectedIndex ? 'text-white/80' : 'text-slate-600'
                                }`}>
                                  📞 {suggestion.phone}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-3">
                              {suggestion.source && (
                                <Badge variant={index === selectedIndex ? "secondary" : "outline"} className="text-xs whitespace-nowrap">
                                  {suggestion.source}
                                </Badge>
                              )}
                              <Badge variant="secondary" className="text-xs">
                                {suggestion.total_rentals || 0}
                              </Badge>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Selected Customer Card - COMPACT by default */}
              {customer && (
                <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 animate-fade-in">
                  {/* MAIN ROW: Name + Eye Toggle + Change Button */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-slate-900 text-lg truncate">{customer.name}</p>
                          {customerHistory?.has_alerts && (
                            <Badge variant="destructive" className="animate-pulse flex-shrink-0">
                              ⚠️
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 font-mono">{customer.dni}</p>
                      </div>
                    </div>
                    
                    {/* Right side: Eye toggle + Badge + Change button */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Eye Toggle Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowCustomerTechData(!showCustomerTechData)}
                        className={`h-9 px-3 gap-1.5 ${showCustomerTechData ? 'bg-blue-500/20 text-blue-700' : 'text-slate-500 hover:text-blue-600'}`}
                        title={showCustomerTechData ? "Ocultar datos" : "Ver datos técnicos"}
                        data-testid="toggle-tech-data-btn"
                      >
                        {showCustomerTechData ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                        <span className="hidden sm:inline text-xs">
                          {showCustomerTechData ? "Ocultar" : "Ver datos"}
                        </span>
                      </Button>
                      
                      <Badge variant="secondary" className="font-semibold hidden md:flex">
                        {customer.total_rentals || 0} alq.
                      </Badge>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCustomer(null);
                          setCustomerHistory(null);
                          setSearchTerm("");
                          setShowCustomerTechData(false);
                          setTimeout(() => searchRef.current?.focus(), 100);
                        }}
                        className="h-9 px-2 text-slate-500 hover:text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* EXPANDABLE SECTION - Only shown when eye is clicked */}
                  {showCustomerTechData && (
                    <div className="mt-4 pt-4 border-t border-primary/20 space-y-3 animate-fade-in">
                      {/* Contact & Source Info */}
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                        {customer.phone && (
                          <span className="flex items-center gap-1">
                            📞 {customer.phone}
                          </span>
                        )}
                        {customer.city && (
                          <span className="flex items-center gap-1">
                            📍 {customer.city}
                          </span>
                        )}
                        {customer.source && (
                          <Badge variant="outline" className="bg-blue-500/10 text-blue-700 border-blue-200">
                            {customer.source}
                            {getProviderDiscount() > 0 && (
                              <span className="ml-1 font-bold">-{getProviderDiscount()}%</span>
                            )}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Technical Data Summary */}
                      {(customer.height || customer.weight || customer.boot_size || customer.ski_level) && (
                        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-200">
                          <p className="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-1">
                            <Mountain className="h-3 w-3" /> Datos Técnicos
                          </p>
                          <div className="flex flex-wrap gap-3 text-sm">
                            {customer.height && (
                              <span className="flex items-center gap-1 text-slate-700">
                                <Ruler className="h-3 w-3 text-blue-500" />
                                <strong>{customer.height}</strong>cm
                              </span>
                            )}
                            {customer.weight && (
                              <span className="flex items-center gap-1 text-slate-700">
                                <Scale className="h-3 w-3 text-blue-500" />
                                <strong>{customer.weight}</strong>kg
                              </span>
                            )}
                            {customer.boot_size && (
                              <span className="flex items-center gap-1 text-slate-700">
                                <Package className="h-3 w-3 text-blue-500" />
                                Pie <strong>{customer.boot_size}</strong>
                              </span>
                            )}
                            {customer.ski_level && customer.ski_level !== 'sin_especificar' && (
                              <Badge className="bg-blue-600 text-white border-0 capitalize text-xs">
                                {customer.ski_level}
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Alerts Section */}
                      {customerHistory?.has_alerts && customerHistory.overdue_rentals > 0 && (
                        <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-semibold text-red-900">
                                Cliente con {customerHistory.overdue_rentals} alquiler(es) vencido(s)
                              </p>
                              <p className="text-xs text-red-700 mt-1">
                                Verifica el estado antes de proceder
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Customer Notes */}
                      {customer.notes && (
                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-200">
                          <p className="text-xs font-semibold text-amber-900 mb-1 flex items-center gap-1">
                            📝 Observaciones
                          </p>
                          <p className="text-sm text-amber-800">{customer.notes}</p>
                        </div>
                      )}

                      {/* Preferred Sizes from History */}
                      {customerHistory?.preferred_sizes && Object.keys(customerHistory.preferred_sizes).length > 0 && (
                        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-200">
                          <p className="text-xs font-semibold text-emerald-900 mb-2 flex items-center gap-1">
                            <History className="h-3 w-3" /> Tallas Habituales
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(customerHistory.preferred_sizes).map(([type, sizes]) => (
                              <Badge key={type} className="bg-emerald-500/20 text-emerald-800 border-emerald-300 text-xs">
                                <strong>{type}:</strong>&nbsp;{sizes.join(", ")}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Create New Customer Button */}
              {!customer && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setShowNewCustomer(true);
                    setNewCustomer({ ...newCustomer, dni: searchTerm.toUpperCase() });
                  }}
                  data-testid="new-customer-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Nuevo Cliente
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Fechas */}
          {/* Smart Dates Card */}
          <Card className="border-slate-200 border-primary/50">
            <CardHeader className="py-2 px-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary" tabIndex={-1} aria-hidden="true" />
                Duración del Alquiler
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 px-4 pb-3">
              {/* Fila principal: Días + Fechas en línea */}
              <div className="flex items-center gap-3 bg-primary/5 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <Label className="text-xs font-medium text-slate-600 whitespace-nowrap">Días:</Label>
                  <Input
                    ref={daysRef}
                    type="number"
                    min="1"
                    value={numDays}
                    onChange={(e) => handleNumDaysChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Tab' && !isEditingCartItem) {
                        e.preventDefault();
                        if (e.shiftKey) {
                          focusPrevField('days');
                        } else {
                          focusNextField('days');
                        }
                        return;
                      }
                      handleDaysKeyDown(e);
                    }}
                    tabIndex={3}
                    className="h-8 text-lg font-bold text-center w-14 border-primary/30"
                    data-testid="num-days-input"
                    autoComplete="off"
                  />
                </div>
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700" tabIndex={-1}>
                  <span>{formatDateDisplay(startDate)}</span>
                  <ArrowRight className="h-3 w-3 text-primary" tabIndex={-1} aria-hidden="true" />
                  <span>{formatDateDisplay(endDate)}</span>
                </div>
                <div className="flex gap-1 ml-auto">
                  {[1, 2, 3, 5, 7].map(d => (
                    <Button
                      key={d}
                      variant={numDays === d ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleNumDaysChange(d)}
                      className="h-6 w-6 p-0 text-xs"
                    >
                      {d}
                    </Button>
                  ))}
                </div>
              </div>

              {showTimeHint && (
                <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded animate-fade-in" tabIndex={-1}>
                  <Clock className="h-3 w-3" tabIndex={-1} aria-hidden="true" />
                  <span>{getTimeHintMessage()}</span>
                </div>
              )}

              {/* Fechas en línea compacta */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 flex-1">
                  <Label className="text-xs text-slate-500">Desde:</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="h-7 text-xs flex-1"
                    data-testid="start-date-input"
                    tabIndex={-1}
                  />
                </div>
                <div className="flex items-center gap-1 flex-1">
                  <Label className="text-xs text-slate-500">Hasta:</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => handleEndDateChange(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="h-7 text-xs flex-1"
                    data-testid="end-date-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Artículos (Búsqueda) - Tercera columna */}
          <Card className="border-slate-200">
            <CardHeader className="py-2 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="h-4 w-4 text-slate-500" />
                  Artículos
                </CardTitle>
                {(items.length > 0 || customer) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearCart}
                    className="h-6 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50 px-2"
                    data-testid="clear-cart-btn"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-2 px-4 pb-3">
              {/* Barcode Input */}
              <div className="relative">
                <Barcode className={`absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors ${globalScannerActive ? 'text-emerald-500 animate-pulse' : 'text-slate-600'}`} tabIndex={-1} aria-hidden="true" />
                <Input
                  ref={barcodeRef}
                  placeholder={globalScannerActive ? "Escáner listo..." : "Código de barras..."}
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Tab' && !isEditingCartItem) {
                      e.preventDefault();
                      if (e.shiftKey) {
                        focusPrevField('barcode');
                      } else {
                        focusNextField('barcode');
                      }
                      return;
                    }
                    addItemByBarcode(e);
                  }}
                  tabIndex={1}
                  className={`h-9 pl-8 text-sm font-mono transition-all ${
                    globalScannerActive 
                      ? 'border-emerald-400 ring-1 ring-emerald-200 bg-emerald-500/10' 
                      : ''
                  }`}
                  data-testid="barcode-input"
                  autoComplete="off"
                />
              </div>
              
              {/* Consumo Estructurado — chips from the web reservation to
                  prefill the item search modal in one click. Only shows when
                  the booking carries a structured items_detail payload. */}
              {onlineBooking?.items_detail?.length > 0 && (
                <div
                  className="rounded-lg border border-blue-200 bg-blue-500/5 p-2 space-y-1.5"
                  data-testid="booking-suggestions-panel"
                >
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-blue-700">
                    <Globe className="h-3 w-3" />
                    Sugerencias de la reserva
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {onlineBooking.items_detail.map((entry, idx) => {
                      const rawTipo = String(entry.tipo || entry.type || "").trim();
                      const rawTalla = String(entry.talla || entry.size || "").trim();
                      const rawCantidad = Number(entry.cantidad || entry.quantity || 1) || 1;
                      if (!rawTipo && !rawTalla) return null;
                      const tipoLower = rawTipo.toLowerCase();
                      const match = itemTypes.find(
                        (t) =>
                          t.value !== "all" &&
                          (
                            t.label.toLowerCase().includes(tipoLower) ||
                            tipoLower.includes(t.label.toLowerCase()) ||
                            t.value.toLowerCase().includes(tipoLower)
                          )
                      );
                      const chipLabel = [rawTipo || match?.label, rawTalla].filter(Boolean).join(" ");
                      return (
                        <button
                          key={`booking-sugg-${idx}`}
                          type="button"
                          onClick={() => {
                            setItemSearchType(match?.value || "all");
                            setItemSearchTerm(rawTalla || rawTipo || "");
                            setShowItemSearch(true);
                          }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 transition-colors"
                          data-testid={`booking-suggestion-${idx}`}
                        >
                          {chipLabel || "Ver reserva"}
                          {rawCantidad > 1 && (
                            <span className="text-blue-500">× {rawCantidad}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Botón Buscar */}
              <Button 
                variant="outline" 
                onClick={() => setShowItemSearch(true)}
                className="w-full h-8 text-sm"
                data-testid="manual-search-btn"
                tabIndex={-1}
              >
                <Search className="h-3.5 w-3.5 mr-2" tabIndex={-1} aria-hidden="true" />
                Buscar
              </Button>
              
              {/* Indicador */}
              {hasCartData && (
                <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300 bg-emerald-500/10 w-full justify-center">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {items.length} guardado{items.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </CardContent>
          </Card>
        </div>

        {/* FILA 2: Artículos Seleccionados (Ancho Completo) */}
        <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5 text-slate-500" />
                Artículos Seleccionados
                {items.length > 0 && <Badge className="ml-2">{items.length}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* LISTA DE ARTÍCULOS - Grid navegable por teclado */}
              <div ref={cartGridRef} className="max-h-[400px] overflow-y-auto keyboard-nav-grid">
                {items.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-48 text-slate-600">
                    <Package className="h-12 w-12 mb-2" />
                    <p>Escanea artículos para añadirlos</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* GROUPED CART ITEMS - Solo PADRES (packs) e items sueltos */}
                    {/* Los HIJOS ya fueron filtrados en getGroupedCartItems() */}
                    {getGroupedCartItems().map((group, groupIndex) => {
                      if (group.type === 'pack') {
                        // PACK: Línea principal con selector + componentes expandibles
                        const packTotal = group.price;
                        const isExpanded = expandedPackId === group.packId;
                        
                        return (
                          <div 
                            key={group.packId}
                            className="rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-400 animate-fade-in overflow-hidden"
                          >
                            {/* LÍNEA PRINCIPAL DEL PACK - Con Selector de Definición */}
                            <div className="grid grid-cols-12 gap-2 items-center py-2.5 px-3">
                              {/* Selector de Pack (Dropdown) */}
                              <div className="col-span-5">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => setExpandedPackId(isExpanded ? null : group.packId)}
                                    className="p-1 hover:bg-amber-200 rounded transition-colors"
                                    title={isExpanded ? "Colapsar componentes" : "Expandir componentes"}
                                  >
                                    <ChevronDown className={`h-5 w-5 text-amber-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                  </button>
                                  <Select
                                    value={group.pack._id || group.pack.id}
                                    onValueChange={(newPackId) => handlePackDefinitionChange(group.items, newPackId, group.packId)}
                                  >
                                    <SelectTrigger 
                                      className="flex-1 h-9 bg-white border-amber-300 font-bold text-amber-800"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <SelectValue placeholder="Seleccionar pack" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {packs.map(p => (
                                        <SelectItem key={p._id || p.id} value={p._id || p.id}>
                                          {p.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <span className="font-mono font-bold text-slate-900 text-sm sm:text-base whitespace-nowrap">({group.childCodes})</span>
                                </div>
                              </div>
                              
                              {/* Días del Pack - Editable */}
                              <div className="col-span-3 text-center">
                                {editingItemDays === group.packId ? (
                                  <Input
                                    type="number"
                                    min="1"
                                    step="1"
                                    defaultValue={group.days}
                                    className="h-8 w-20 text-center text-sm font-bold mx-auto border-2 border-amber-500 bg-amber-500/10"
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                      e.stopPropagation();
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        updatePackDays(group.items, e.target.value);
                                      }
                                      if (e.key === 'Escape') {
                                        e.preventDefault();
                                        setEditingItemDays(null);
                                      }
                                    }}
                                    onBlur={(e) => updatePackDays(group.items, e.target.value)}
                                    data-testid={`edit-pack-days-${group.packId}`}
                                  />
                                ) : (
                                  <Badge 
                                    className="cursor-pointer bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm px-4 py-1"
                                    onClick={() => setEditingItemDays(group.packId)}
                                  >
                                    {group.days} días <Edit2 className="h-3 w-3 ml-1 inline" />
                                  </Badge>
                                )}
                              </div>
                              
                              {/* Precio Total Pack (IVA incluido) - EDITABLE */}
                              <div className="col-span-3 text-right">
                                <p className="text-xs text-amber-700 uppercase">Total (IVA inc.)</p>
                                {editingPackPrice === group.packId ? (
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    defaultValue={packTotal.toFixed(2)}
                                    className="h-8 w-24 text-right text-lg font-bold ml-auto border-2 border-orange-500 bg-orange-50"
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                      e.stopPropagation();
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        updatePackPrice(group.items, e.target.value);
                                      }
                                      if (e.key === 'Escape') {
                                        e.preventDefault();
                                        setEditingPackPrice(null);
                                      }
                                    }}
                                    onBlur={(e) => updatePackPrice(group.items, e.target.value)}
                                    data-testid={`edit-pack-price-${group.packId}`}
                                  />
                                ) : (
                                  <div 
                                    className="cursor-pointer hover:bg-amber-500/20 rounded px-2 py-1 inline-block"
                                    onClick={() => setEditingPackPrice(group.packId)}
                                  >
                                    <p className={`text-xl font-bold ${group.isEdited ? 'text-orange-600' : 'text-amber-700'}`}>
                                      {formatCurrency(packTotal)}
                                      <Edit2 className="h-3 w-3 ml-1 inline opacity-50" />
                                    </p>
                                    {group.isEdited && (
                                      <div className="flex items-center gap-1 mt-1">
                                        <Badge className="bg-orange-500 text-white text-xs">EDITADO</Badge>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-5 px-1 text-xs text-slate-500 hover:text-blue-600"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            resetPackPrice(group.items);
                                          }}
                                          title="Restaurar precio de tarifa"
                                        >
                                          <RotateCcw className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* Remove Pack Button */}
                              <div className="col-span-1 text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => removePackComplete(group.items)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* COMPONENTES INTERNOS (Expandibles) */}
                            {isExpanded && (
                              <div className="border-t border-amber-300 bg-amber-500/10/50 px-3 py-2 space-y-2">
                                <p className="text-xs text-amber-700 font-semibold uppercase mb-2">
                                  Componentes del Pack (edita la etiqueta para el ticket)
                                </p>
                                {group.items.map((componentItem, idx) => {
                                  // Usar customTypeLabel si existe, sino el label del tipo
                                  const typeLabel = componentItem.customTypeLabel || 
                                    itemTypes.find(t => t.value === componentItem.item_type)?.label || 
                                    componentItem.item_type;
                                  
                                  return (
                                    <div 
                                      key={componentItem.barcode}
                                      className="grid grid-cols-12 gap-2 items-center py-1.5 px-2 bg-white/60 rounded-lg border border-amber-200"
                                    >
                                      {/* Código del componente */}
                                      <div className="col-span-3">
                                        <p className="text-[10px] text-slate-500 uppercase">Código</p>
                                        <p className="font-mono font-bold text-slate-900 text-base sm:text-lg">
                                          {componentItem.internal_code || componentItem.barcode}
                                        </p>
                                      </div>
                                      
                                      {/* Tipo - INPUT DE TEXTO EDITABLE (solo para el ticket) */}
                                      <div className="col-span-5">
                                        <p className="text-[10px] text-amber-700 uppercase">Tipo (etiqueta ticket)</p>
                                        <input
                                          type="text"
                                          defaultValue={typeLabel}
                                          placeholder="Ej: Botas (Traídas por cliente)"
                                          className="w-full h-7 px-2 text-sm font-semibold text-slate-800 bg-white border border-amber-300 rounded focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors"
                                          onClick={(e) => e.stopPropagation()}
                                          onKeyDown={(e) => {
                                            e.stopPropagation();
                                            if (e.key === 'Enter') {
                                              e.target.blur();
                                            }
                                            if (e.key === 'Escape') {
                                              e.target.value = typeLabel;
                                              e.target.blur();
                                            }
                                          }}
                                          onBlur={(e) => {
                                            const newLabel = e.target.value.trim();
                                            if (newLabel !== typeLabel) {
                                              handleComponentTypeLabelChange(componentItem.barcode, newLabel);
                                            }
                                          }}
                                          data-testid={`component-type-label-${componentItem.barcode}`}
                                        />
                                      </div>
                                      
                                      {/* Info adicional */}
                                      <div className="col-span-4 text-right">
                                        <p className="text-[10px] text-slate-500 uppercase">Artículo</p>
                                        <p className="text-xs text-slate-600 truncate">
                                          {componentItem.brand} {componentItem.model} {componentItem.size && `(${componentItem.size})`}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      } else {
                        // SINGLE ITEM: Render normally - TARIFA ESCALONADA
                        const item = group.item;
                        const cartKey = item.cartId || item.id || item.barcode;
                        const days = group.days;
                        // Precio por línea (sin multiplicar por cantidad - cada línea es independiente)
                        const totalItemPrice = group.price;
                        
                        return (
                          <div 
                            key={cartKey}
                            className={`grid grid-cols-12 gap-2 items-center py-2 px-3 rounded-xl transition-colors animate-fade-in ${
                              item.is_generic ? 'bg-emerald-500/10 hover:bg-emerald-500/20' : 'bg-slate-50 hover:bg-slate-100'
                            }`}
                          >
                            {/* Nombre/Código */}
                            <div className="col-span-4">
                              <p className="text-[10px] text-slate-500 font-medium uppercase">
                                {item.is_generic ? 'Artículo' : 'Código'}
                              </p>
                              {item.is_generic ? (
                                <p className="font-bold text-emerald-700 truncate text-sm">{item.name}</p>
                              ) : (
                                <>
                                  <p className="font-mono font-bold text-slate-900 text-base sm:text-lg">{item.internal_code || item.barcode}</p>
                                  <p className="text-[10px] text-slate-500 leading-tight">{item.brand} {item.model} {item.size && `(${item.size})`}</p>
                                </>
                              )}
                            </div>
                            
                            {/* Tipo - Editable */}
                            <div className="col-span-2">
                              <p className="text-[10px] text-slate-500 font-medium uppercase">Tipo</p>
                              {editingItemType === cartKey ? (
                                <Select
                                  value={item.item_type}
                                  onValueChange={(newType) => handleItemTypeChange(cartKey, newType)}
                                  onOpenChange={(open) => !open && setEditingItemType(null)}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {itemTypes.map(type => (
                                      <SelectItem key={type.value} value={type.value}>
                                        {type.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Badge 
                                  variant="outline" 
                                  className="font-semibold text-xs cursor-pointer hover:bg-slate-100"
                                  onClick={() => setEditingItemType(cartKey)}
                                >
                                  {itemTypes.find(t => t.value === item.item_type)?.label || item.item_type}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Días - Editable independiente por línea */}
                            <div className="col-span-2 text-center">
                              <p className="text-[10px] text-slate-500 font-medium uppercase">Días</p>
                              {editingItemDays === cartKey ? (
                                <Input
                                  type="number"
                                  min="1"
                                  step="1"
                                  defaultValue={days}
                                  className="h-6 w-14 text-center text-sm font-bold mx-auto border-2 border-blue-500 bg-blue-500/10"
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      updateItemDays(cartKey, e.target.value);
                                    }
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      setEditingItemDays(null);
                                    }
                                  }}
                                  onBlur={(e) => updateItemDays(cartKey, e.target.value)}
                                  data-testid={`edit-days-${cartKey}`}
                                />
                              ) : (
                                <Badge 
                                  variant="outline" 
                                  className="cursor-pointer hover:bg-blue-500/20 font-bold text-blue-700 border-blue-300 text-xs"
                                  onClick={() => setEditingItemDays(cartKey)}
                                >
                                  {days}d <Edit2 className="h-2.5 w-2.5 ml-0.5 inline" />
                                </Badge>
                              )}
                            </div>
                            
                            {/* Precio Total (IVA incluido) - EDITABLE */}
                            <div className="col-span-3 text-right">
                              <p className="text-[10px] text-slate-500 font-medium uppercase">Total</p>
                              {group.price === 0 && !item.is_generic ? (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertCircle className="h-3 w-3 mr-1" /> Sin tarifa
                                </Badge>
                              ) : editingItemPrice === cartKey ? (
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  defaultValue={totalItemPrice.toFixed(2)}
                                  className="h-8 w-24 text-right text-lg font-bold ml-auto border-2 border-orange-500 bg-orange-50"
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      updateItemPrice(cartKey, e.target.value);
                                    }
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      setEditingItemPrice(null);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    updateItemPrice(cartKey, e.target.value);
                                  }}
                                  data-testid={`edit-price-${cartKey}`}
                                />
                              ) : (
                                <div 
                                  className="cursor-pointer hover:bg-slate-100 rounded px-2 py-1 inline-block"
                                  onClick={() => setEditingItemPrice(cartKey)}
                                >
                                  <p className={`text-lg font-bold ${group.isEdited ? 'text-orange-600' : 'text-slate-900'}`}>
                                    {formatCurrency(totalItemPrice)}
                                    <Edit2 className="h-3 w-3 ml-1 inline opacity-50" />
                                  </p>
                                  {group.isEdited && (
                                    <Badge className="bg-orange-500 text-white text-xs mt-1">EDITADO</Badge>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Eliminar */}
                            <div className="col-span-1 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeItem(cartKey)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>
                )}
              </div>

              {/* Payment Inputs Section */}
              <div className="mt-6 pt-6 border-t-2 border-slate-200">
                {/* QUICK ADD: Dynamic buttons from DB */}
              {quickAddItems.length > 0 && (
                <div className="mb-4 pb-4 border-b border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="h-4 w-4 text-amber-500" />
                    <span className="text-sm text-slate-600 font-medium">Añadir rápido:</span>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-slate-200">
                    {quickAddItems.map(item => (
                      <Button
                        key={item.id}
                        variant="outline"
                        size="sm"
                        onClick={() => quickAddItem(item)}
                        className="h-8 px-3 text-sm whitespace-nowrap hover:bg-emerald-500/10 hover:border-emerald-300 hover:text-emerald-700 flex-shrink-0"
                        data-testid={`quick-add-${item.id}`}
                        disabled={item.is_generic ? item.stock_available < 1 : item.status !== 'available'}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1.5" />
                        {item.is_generic 
                          ? `${item.name || item.item_type} (${item.stock_available})`
                          : item.internal_code || item.barcode
                        }
                        {item.rental_price > 0 && (
                          <span className="ml-1 text-xs text-slate-600">{formatCurrency(item.rental_price)}</span>
                        )}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              {quickAddItems.length === 0 && !loadingQuickAdd && (
                <div className="mb-4 pb-4 border-b border-slate-200 text-center">
                  <p className="text-xs text-slate-600">
                    💡 Marca artículos en Inventario con "Mostrar en Añadir Rápido" para verlos aquí
                  </p>
                </div>
              )}

              {hasDiscount && (
                <div className="mb-4">
                  <Input
                    placeholder="Motivo del descuento (opcional): grupo, VIP, etc."
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    className="h-8 text-sm"
                    tabIndex={15}
                  />
                </div>
              )}
              </div>
            </CardContent>
          </Card>
      </div>

      {/* FOOTER - Resumen, Pagos y Botón - SIEMPRE FIJO EN PANTALLA */}
      <div className="fixed bottom-[23px] right-4 bg-white border-2 border-slate-200 rounded-xl shadow-[0_-4px_20px_rgba(0,0,0,0.15)] z-50 left-[272px]">
        <div className="px-4 py-2">
          <div className="flex items-center gap-3">
            {/* Descuento de Proveedor */}
            {customer?.source && getProviderDiscount() > 0 && (
              <div className="flex-shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg bg-blue-500/10 border border-blue-200">
                <span className="text-xs text-blue-700">{customer.source}</span>
                <Badge className="bg-blue-600 text-white text-xs px-1.5 py-0">
                  -{getProviderDiscount()}%
                </Badge>
              </div>
            )}
            
            {/* Total */}
            <div className="flex-shrink-0">
              <p className="text-[10px] text-slate-500 uppercase font-medium">Total</p>
              <p className="text-lg font-bold text-slate-900" data-testid="grand-total-display">{formatCurrency(total)}</p>
            </div>
            
            <div className="h-8 w-px bg-slate-200 flex-shrink-0"></div>
            
            {/* Depósito */}
            <div className="flex-shrink-0 w-20">
              <p className="text-[10px] text-slate-500 uppercase font-medium mb-0.5">Depósito</p>
              <Input
                type="number"
                placeholder="0"
                value={deposit}
                onChange={(e) => setDeposit(e.target.value)}
                className="h-8 text-xs"
                data-testid="deposit-input"
              />
            </div>
            
            {/* Descuento */}
            <div className="flex-shrink-0">
              <p className="text-[10px] text-slate-500 uppercase font-medium mb-0.5">Dto.</p>
              <div className="flex gap-1">
                <Select value={discountType} onValueChange={setDiscountType}>
                  <SelectTrigger className="w-14 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-</SelectItem>
                    <SelectItem value="percent">%</SelectItem>
                    <SelectItem value="fixed">{getCurrencySymbol()}</SelectItem>
                  </SelectContent>
                </Select>
                {discountType !== 'none' && (
                  <Input
                    type="number"
                    placeholder="0"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="h-8 w-16 text-xs"
                  />
                )}
              </div>
            </div>
            
            {/* Resumen dinámico */}
            {(() => {
              const depositAmount = Number(parseFloat(deposit) || 0);
              // El descuento YA está aplicado en `total` (total = subtotal - descuento).
              // Aquí solo calculamos el IMPORTE del descuento para mostrarlo como badge informativo.
              const appliedDiscount = subtotal - total; // Diferencia real entre subtotal y total
              // Web-prepaid amount (Cobro Online Cotejado). Bounded to what the
              // customer actually owes so a mis-entered web amount can never
              // produce a negative "A Cobrar".
              const prepaidOnline = Number(onlineBooking?.amount) || 0;
              const rawDue = total + depositAmount;
              const finalTotal = Math.max(0, rawDue - prepaidOnline);

              return (
                <div className="flex items-center gap-2 ml-auto">
                  {depositAmount > 0 && (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                      +{formatCurrency(depositAmount)} dep.
                    </Badge>
                  )}
                  {appliedDiscount > 0 && (
                    <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                      -{discountType === 'percent' ? `${discountValue}%` : `${formatCurrency(appliedDiscount)}`}
                    </Badge>
                  )}
                  {prepaidOnline > 0 && (
                    <Badge
                      variant="outline"
                      className="text-xs text-emerald-700 border-emerald-300 bg-emerald-50"
                      data-testid="online-prepaid-badge"
                    >
                      Web -{formatCurrency(prepaidOnline)}
                    </Badge>
                  )}
                  <Badge variant="secondary" className="text-xs">
                    {items.length} art.
                  </Badge>
                  {(depositAmount > 0 || appliedDiscount > 0 || prepaidOnline > 0) && (
                    <div className="text-right">
                      <p className="text-[10px] text-slate-500 uppercase">A Cobrar</p>
                      <p className="text-base font-bold text-primary" data-testid="amount-due-display">{formatCurrency(finalTotal)}</p>
                    </div>
                  )}
                </div>
              );
            })()}
            
            {/* Botón de Acción */}
            <Button
              ref={submitRef}
              size="lg"
              onClick={completeRental}
              disabled={loading || !customer || items.length === 0}
              tabIndex={4}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && !isEditingCartItem) {
                  e.preventDefault();
                  if (e.shiftKey) {
                    focusPrevField('submit');
                  } else {
                    focusNextField('submit');
                  }
                  return;
                }
              }}
              className="h-10 px-6 text-sm font-semibold shadow-lg hover:shadow-xl transition-all flex-shrink-0"
              data-testid="complete-rental-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Completar
            </Button>
          </div>
        </div>
      </div>

      {/* New Customer Dialog - With Focus Trap */}
      <Dialog open={showNewCustomer} onOpenChange={setShowNewCustomer}>
        <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Nuevo Cliente</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>DNI/Pasaporte *</Label>
                  <Input
                    value={newCustomer.dni}
                    onChange={(e) => setNewCustomer({ ...newCustomer, dni: e.target.value.toUpperCase() })}
                    className="h-11 mt-1"
                    data-testid="new-customer-dni"
                    autoFocus
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    className="h-11 mt-1"
                    data-testid="new-customer-phone"
                    autoComplete="off"
                  />
                </div>
              </div>
            <div>
              <Label>Nombre Completo *</Label>
              <Input
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                className="h-11 mt-1"
                data-testid="new-customer-name"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                className="h-11 mt-1"
                placeholder="email@ejemplo.com"
                autoComplete="off"
              />
            </div>
            <div>
              <Label>Dirección</Label>
              <Input
                value={newCustomer.address}
                onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                className="h-11 mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Población</Label>
                <Input
                  value={newCustomer.city}
                  onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                  className="h-11 mt-1"
                />
              </div>
              <div>
                <Label>Proveedor/Fuente</Label>
                <Select 
                  value={newCustomer.source || "none"} 
                  onValueChange={(v) => setNewCustomer({ ...newCustomer, source: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="h-11 mt-1">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin especificar</SelectItem>
                    <SelectItem value="Web propia">Web propia</SelectItem>
                    <SelectItem value="Booking.com">Booking.com</SelectItem>
                    <SelectItem value="Expedia">Expedia</SelectItem>
                    <SelectItem value="Hotel">Hotel</SelectItem>
                    <SelectItem value="Recomendación">Recomendación</SelectItem>
                    <SelectItem value="Walk-in">Walk-in (pie de tienda)</SelectItem>
                    {sources.map(s => (
                      <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCustomer(false)}>
              Cancelar
            </Button>
            <Button onClick={createNewCustomer} data-testid="save-new-customer-btn">
              Guardar Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Item Search Dialog */}
      <Dialog open={showItemSearch} onOpenChange={(open) => {
        setShowItemSearch(open);
        if (!open) {
          setSearchFilter(null);
          setItemSearchType("all");
          setItemSearchTerm("");
        }
      }}>
        <DialogContent className="sm:max-w-2xl h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              {searchFilter ? (
                <>
                  <Sparkles className="h-5 w-5 text-amber-500" />
                  Completar Pack - Buscar {itemTypes.find(t => t.value === searchFilter.type)?.label || searchFilter.type}
                </>
              ) : (
                "Buscar Artículo Manualmente"
              )}
            </DialogTitle>
            {searchFilter && (
              <p className="text-sm text-amber-600">
                Mostrando artículos de tipo <strong>{itemTypes.find(t => t.value === searchFilter.type)?.label}</strong> para completar el pack
              </p>
            )}
          </DialogHeader>
          
          {/* Search Filters */}
          <div className="grid grid-cols-3 gap-3 py-4 flex-shrink-0">
            <Input
              placeholder="Código, marca, modelo..."
              value={itemSearchTerm}
              onChange={(e) => setItemSearchTerm(e.target.value)}
              className="h-11"
              autoFocus
            />
            <Select value={itemSearchType} onValueChange={setItemSearchType}>
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                {itemTypes.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Results - VIRTUALIZADO para rendimiento con scroll contenido */}
          <div className="flex-1 min-h-0 overflow-hidden border rounded-lg">
            {searchingItems ? (
              <div className="p-4">
                <SearchResultsSkeleton count={5} />
              </div>
            ) : searchResults.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No se encontraron artículos disponibles</p>
            ) : (
              <VirtualizedSearchResults 
                items={searchResults}
                onItemClick={addItemFromSearch}
              />
            )}
          </div>

          <DialogFooter className="flex-shrink-0 pt-4">
            <Button variant="outline" onClick={() => {
              setShowItemSearch(false);
              refocusBarcodeInput(); // Auto-focus barcode after closing search
            }}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto Cash Opening Dialog - PRIORITY (Before Payment) */}
      <Dialog open={showAutoOpenCashDialog} onOpenChange={(open) => {
        // Don't allow closing without completing
        if (!open && !openingCashBalance) {
          toast.error("Debes introducir el fondo de caja para continuar");
          return;
        }
        setShowAutoOpenCashDialog(open);
      }}>
        <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              🏪 Iniciando Nueva Jornada
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Alert Info */}
            <div className="p-4 rounded-lg bg-amber-500/10 border-2 border-amber-300">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-amber-900">No hay caja abierta</p>
                  <p className="text-sm text-amber-800 mt-1">
                    Para procesar este cobro, necesitas abrir la caja del día.
                  </p>
                </div>
              </div>
            </div>

            {/* Opening Balance Input */}
            <div className="space-y-3">
              <Label className="text-base font-bold text-slate-900">
                Fondo de Caja Inicial (Efectivo) *
              </Label>
              <p className="text-sm text-slate-600">
                Introduce el dinero en efectivo con el que empiezas la jornada
              </p>
              <Input
                type="number"
                value={openingCashBalance}
                onChange={(e) => setOpeningCashBalance(e.target.value)}
                placeholder="0.00"
                className="h-16 text-3xl font-bold text-center"
                min="0"
                step="0.01"
                autoFocus
              />
              <p className="text-xs text-slate-500">
                Ejemplo: Si tienes {formatCurrency(100)} en la caja para empezar, introduce <span className="font-mono font-bold">100</span>
              </p>
            </div>

            {/* Preview */}
            {openingCashBalance && (
              <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 border-2 border-emerald-300 animate-fade-in">
                <p className="text-sm text-emerald-700 font-medium">Resumen de Apertura</p>
                <p className="text-3xl font-black text-emerald-900 mt-2">
                  Fondo: {formatCurrency(parseFloat(openingCashBalance))}
                </p>
                {pendingPaymentData && (
                  <p className="text-sm text-emerald-700 mt-2">
                    + Primera venta: {formatCurrency(pendingPaymentData.total)}
                  </p>
                )}
              </div>
            )}

            {/* Info box */}
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-200">
              <p className="text-xs text-blue-800">
                💡 <strong>Nota:</strong> Este proceso solo ocurre una vez al día. Las siguientes ventas se procesarán automáticamente.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button 
              onClick={openCashAndContinue}
              disabled={!openingCashBalance && openingCashBalance !== "0"}
              className="bg-emerald-600 hover:bg-emerald-700 w-full"
              size="lg"
            >
              <CheckCircle className="h-5 w-5 mr-2" />
              Abrir Caja y Continuar con el Cobro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog - NEW */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              💳 Finalizar Pago
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Total to Pay - Highlighted (INCLUYE DEPÓSITO) */}
            <div className="p-4 rounded-lg bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-300">
              <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide">Total a Pagar</p>
              <p className="text-3xl font-black text-emerald-900 mt-1">
                {formatCurrency(calculateTotalToPay())}
              </p>
            </div>

            {/* MIXED PAYMENT INPUT */}
            <MixedPaymentInput
              total={calculateTotalToPay()}
              onChange={(state) => setMixedPaymentState(state)}
              showPending={true}
              showOnline={true}
            />

          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowPaymentDialog(false);
              }}
              disabled={processingPayment}
              size="sm"
            >
              Cancelar
            </Button>
            <Button 
              onClick={processPaymentAndCompleteRental}
              disabled={processingPayment || !mixedPaymentState?.isValid}
              className="bg-emerald-600 hover:bg-emerald-700"
              size="sm"
            >
              {processingPayment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {mixedPaymentState?.mode === "pending" ? "Guardar" : "Cobrar"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Dialog with Print Button */}
      <Dialog open={showSuccessDialog} onOpenChange={closeSuccessDialog}>
        <DialogContent className="sm:max-w-md overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600 text-xl">
              <CheckCircle className="h-7 w-7" />
              ¡Alquiler Completado!
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-6 space-y-4">
            <div className="p-4 rounded-lg bg-emerald-500/10 border-2 border-emerald-200 w-full max-w-full box-border">
              <p className="text-sm font-semibold text-emerald-900 break-words">
                <strong>Cliente:</strong> {completedRental?.customer_name}
              </p>
              <p className="text-sm font-semibold text-emerald-900 mt-2">
                <strong>Total pagado:</strong> {formatCurrency(completedRental?.paid_amount)}
              </p>
              <p className="text-xs text-emerald-700 mt-2 font-mono">
                ID: {completedRental?.id?.substring(0, 8)}
              </p>
            </div>
            
            <Button 
              onClick={printRentalTicket}
              className="w-full h-16 text-xl font-bold bg-primary hover:bg-primary/90 shadow-lg"
              size="lg"
            >
              <Printer className="h-6 w-6 mr-3" />
              🖨️ IMPRIMIR TICKET
            </Button>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={closeSuccessDialog} 
              className="w-full"
            >
              Continuar sin imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
